import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Scale, Calendar, CalendarDays, CalendarRange, ChevronRight, ChevronDown, Upload, Download, Loader2,
  AlertTriangle, X, SlidersHorizontal, FileText, FileSpreadsheet, BookOpen, Trash2, Eye,
} from 'lucide-react';
import Select from 'react-select';
import * as XLSX from 'xlsx-js-style';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Avatar,
  Paper,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormLabel,
  Button,
  Divider,
  Collapse,
  IconButton,
  LinearProgress,
  Tabs,
  Tab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  AttachMoney,
  CalendarToday,
  Business,
  Description,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  ExpandMore,
  PictureAsPdf,
  CloudUpload,
  Delete,
  DeleteOutline,
  Info,
} from '@mui/icons-material';
import { useCompany } from '../../contexts/CompanyContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import AuthService from '../../services/authService';
import TrialBalanceFromFileTab from './TrialBalanceFromFileTab';
import TrialBalanceSheetView from './TrialBalanceSheetView';
import { EDEFTER_API_URL } from '../../config/edefterApi';
import { SectionHeader } from '../../components/common/SectionHeader';

const cardClass =
  'bg-white dark:bg-[#1C1C1E] rounded-[28px] border border-gray-100 dark:border-white/5 shadow-sm';

const inputClass =
  'w-full h-12 px-4 rounded-xl border text-sm transition-all outline-none ' +
  'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 ' +
  'text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 ' +
  'focus:border-[#24299d] focus:ring-2 focus:ring-[#24299d]/20 dark:focus:border-[#5a61e0]';

const getSelectStyles = (darkMode) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 48,
    borderRadius: 12,
    borderColor: state.isFocused ? '#24299d' : darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(36,41,157,0.2)' : 'none',
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
    '&:hover': { borderColor: '#24299d' },
    cursor: state.isDisabled ? 'not-allowed' : 'default',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    backgroundColor: darkMode ? '#1C1C1E' : 'white',
    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    backgroundColor: state.isSelected
      ? '#24299d'
      : state.isFocused
        ? darkMode ? 'rgba(255,255,255,0.08)' : '#f3f4f6'
        : 'transparent',
    color: state.isSelected ? 'white' : darkMode ? '#e5e7eb' : '#111827',
    cursor: 'pointer',
    margin: '2px 4px',
    width: 'calc(100% - 8px)',
  }),
  singleValue: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  placeholder: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  input: (base) => ({ ...base, color: darkMode ? 'white' : '#111827' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: darkMode ? '#6b7280' : '#9ca3af' }),
  clearIndicator: (base) => ({
    ...base, color: darkMode ? '#6b7280' : '#9ca3af',
    '&:hover': { color: '#ef4444' },
  }),
});

const getTableHeadRowSx = (darkMode) => ({
  '& .MuiTableCell-root': {
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
    color: darkMode ? '#9ca3af' : 'inherit',
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f3f4f6',
    fontWeight: 700,
    zIndex: 3,
  },
});

const getTableContainerSx = (darkMode, opts = {}) => ({
  maxHeight: opts.maxHeight || 'calc(100vh - 200px)',
  overflow: 'auto',
  ...(opts.width ? { width: opts.width } : {}),
  backgroundColor: darkMode ? '#1C1C1E' : 'transparent',
  scrollbarColor: darkMode ? '#3f3f46 #1c1c1e' : undefined,
  '&::-webkit-scrollbar': darkMode ? { width: 8, height: 8 } : {},
  '&::-webkit-scrollbar-track': darkMode ? { background: '#1c1c1e' } : {},
  '&::-webkit-scrollbar-thumb': darkMode ? { background: '#3f3f46', borderRadius: 4 } : {},
  '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
    backgroundColor: darkMode ? '#2C2C2E' : '#f9fafb',
  },
});

const getNestedTableHeadRowSx = (darkMode) => ({
  '& .MuiTableCell-root': {
    backgroundColor: darkMode ? '#252528' : 'rgba(25, 118, 210, 0.05)',
    color: darkMode ? '#9ca3af' : undefined,
    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : undefined,
  },
});

const getTotalsRowSx = (darkMode) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 10,
  backgroundColor: darkMode ? '#2C2C2E' : '#e3f2fd',
  borderTop: '2px solid',
  borderColor: darkMode ? 'rgba(255,255,255,0.12)' : '#24299d',
  boxShadow: darkMode ? '0 -2px 8px rgba(0,0,0,0.35)' : '0 -2px 8px rgba(0,0,0,0.1)',
  '& td': {
    fontWeight: 700,
    fontSize: '0.85rem',
    backgroundColor: darkMode ? '#2C2C2E' : '#e3f2fd',
    color: darkMode ? '#e5e7eb' : 'inherit',
  },
});

const extractPdfFileName = (response, identifier, periodStart, periodEnd) => {
  const cd = response.headers.get('Content-Disposition');
  if (cd) {
    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        // fall through
      }
    }
    const match = cd.match(/filename="?([^";]+)"?/i);
    if (match?.[1]) {
      return match[1];
    }
  }
  if (identifier && periodStart && periodEnd) {
    return `${identifier.replaceAll(/[^a-zA-Z0-9]/g, '_')}_${periodStart.replaceAll(/[^a-zA-Z0-9]/g, '_')}_${periodEnd.replaceAll(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  }
  return 'document.pdf';
};

// TDH/THP kod-ad hiyerarşisi backend'den (`/tdh-list`) gelir.
// Frontend'de sabit THP listesi tutulmaz; sıralama ve isimler DB kaynağına göre üretilir.

// THP Aktif: sourceData'dan 3-digit bakiyeleri topla, 2/1 haneli toplamları hesapla
function getThpAktifBalanceMapAndVal(sourceData, selectedPeriod) {
  const balanceMap = Object.create(null);
  if (sourceData && Array.isArray(sourceData)) {
    sourceData.forEach((group) => {
      const accountCode = (group.accountCode || '').trim();
      const baseCode = (accountCode.split('.')[0] || accountCode).trim();
      const base3 = baseCode.length > 3 ? baseCode.substring(0, 3) : baseCode;
      const ilkHane = base3.charAt(0);
      if (ilkHane !== '1' && ilkHane !== '2') return;
      const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
      const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
      let bilancoDegeri = debitBalance - creditBalance;
      const shouldInvert = selectedPeriod === '4' && (ilkHane === '1' || ilkHane === '2');
      if (shouldInvert) bilancoDegeri = -bilancoDegeri;
      balanceMap[base3] = (balanceMap[base3] || 0) + bilancoDegeri;
    });
  }
  const getVal = (code) => {
    if (code.length === 3) return balanceMap[code] != null ? balanceMap[code] : 0;
    if (code.length === 2) {
      return Object.keys(balanceMap).reduce(
        (s, k) => (k.length >= 3 && k.startsWith(code) ? s + (balanceMap[k] || 0) : s),
        0
      );
    }
    if (code.length === 1) {
      return Object.keys(balanceMap).reduce((s, k) => (k.startsWith(code) ? s + (balanceMap[k] || 0) : s), 0);
    }
    return 0;
  };
  return { balanceMap, getVal };
}

function buildAktifThpSonuc(sourceData, selectedPeriod, codeOrder) {
  const { balanceMap, getVal } = getThpAktifBalanceMapAndVal(sourceData, selectedPeriod);
  const balanceKeys = Object.keys(balanceMap);
  // Sadece yevmiye/mizanda verisi olan hesapları göster: 3-digit balanceMap'te varsa, 2-digit altında en az bir 3-digit varsa, 1-digit altında en az bir 2-digit (verili) varsa
  const hasData = (code) => {
    if (code.length === 3) return balanceMap[code] != null;
    if (code.length === 2) return balanceKeys.some((k) => k.length >= 3 && k.startsWith(code));
    if (code.length === 1) {
      return balanceKeys.some((k) => k.startsWith(code));
    }
    return false;
  };
  const sonuc = [];
  let index = 0;
  (codeOrder || []).forEach((t) => {
    if (!hasData(t.code)) return;
    sonuc.push({
      type: 'kalem',
      accountCode: t.code,
      accountName: t.name,
      bilancoDegeri: getVal(t.code),
      ilkHane: t.code.charAt(0),
      index: index++,
    });
  });
  // THP'de "1 Dönen Varlıklar" ve "2 Duran Varlıklar" zaten var; ayrıca toplam satırı eklenmesin (çift yazılmaz)
  return sonuc;
}

// THP Pasif: 3, 4, 5xx bakiyeleri (credit - debit), 2/1 haneli toplamları
function getThpPasifBalanceMapAndVal(sourceData, selectedPeriod) {
  const balanceMap = Object.create(null);
  if (sourceData && Array.isArray(sourceData)) {
    sourceData.forEach((group) => {
      const accountCode = (group.accountCode || '').trim();
      const baseCode = (accountCode.split('.')[0] || accountCode).trim();
      const base3 = baseCode.length > 3 ? baseCode.substring(0, 3) : baseCode;
      const ilkHane = base3.charAt(0);
      if (ilkHane !== '3' && ilkHane !== '4' && ilkHane !== '5') return;
      const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
      const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
      let bilancoDegeri = creditBalance - debitBalance;
      const shouldInvert = selectedPeriod === '4' && (ilkHane === '3' || ilkHane === '4' || ilkHane === '5');
      if (shouldInvert) bilancoDegeri = -bilancoDegeri;
      balanceMap[base3] = (balanceMap[base3] || 0) + bilancoDegeri;
    });
  }
  const getVal = (code) => {
    if (code.length === 3) return balanceMap[code] != null ? balanceMap[code] : 0;
    if (code.length === 2) {
      return Object.keys(balanceMap).reduce(
        (s, k) => (k.length >= 3 && k.startsWith(code) ? s + (balanceMap[k] || 0) : s),
        0
      );
    }
    if (code.length === 1) {
      return Object.keys(balanceMap).reduce((s, k) => (k.startsWith(code) ? s + (balanceMap[k] || 0) : s), 0);
    }
    return 0;
  };
  return { balanceMap, getVal };
}

function buildPasifThpSonuc(sourceData, selectedPeriod, codeOrder) {
  const { balanceMap, getVal } = getThpPasifBalanceMapAndVal(sourceData, selectedPeriod);
  const balanceKeys = Object.keys(balanceMap);
  const hasData = (code) => {
    if (code.length === 3) return balanceMap[code] != null;
    if (code.length === 2) return balanceKeys.some((k) => k.length >= 3 && k.startsWith(code));
    if (code.length === 1) {
      return balanceKeys.some((k) => k.startsWith(code));
    }
    return false;
  };
  const sonuc = [];
  let index = 0;
  (codeOrder || []).forEach((t) => {
    if (!hasData(t.code)) return;
    sonuc.push({
      type: 'kalem',
      accountCode: t.code,
      accountName: t.name,
      bilancoDegeri: getVal(t.code),
      ilkHane: t.code.charAt(0),
      index: index++,
    });
  });
  return sonuc;
}

// THP Gelir: 6xx bakiyeleri (credit - debit; 4. dönemde hareket tutarı + işaret)
function getThpGelirBalanceMapAndVal(sourceData, selectedPeriod) {
  const balanceMap = Object.create(null);
  if (sourceData && Array.isArray(sourceData)) {
    sourceData.forEach((group) => {
      const accountCode = (group.accountCode || '').trim();
      const baseCode = (accountCode.split('.')[0] || accountCode).trim();
      const base3 = baseCode.length > 3 ? baseCode.substring(0, 3) : baseCode;
      if (base3.charAt(0) !== '6') return;
      const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
      const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
      const totalBorc = parseFloat(group.totalBorc || 0);
      const totalAlacak = parseFloat(group.totalAlacak || 0);
      let bilancoDegeri;
      if (selectedPeriod === '4') {
        const magnitude = totalBorc !== 0 ? totalBorc : totalAlacak;
        const codeNum = parseFloat(base3) || 0;
        const isNegatif = (codeNum >= 610 && codeNum <= 639) || (codeNum >= 650 && codeNum <= 669) || (codeNum >= 680 && codeNum <= 689) || codeNum === 691;
        bilancoDegeri = isNegatif ? -magnitude : magnitude;
      } else {
        bilancoDegeri = creditBalance - debitBalance;
      }
      balanceMap[base3] = (balanceMap[base3] || 0) + bilancoDegeri;
    });
  }
  // 4. dönem: 690 = 600-689 toplamı, 692 = 690 + 691
  if (selectedPeriod === '4') {
    const toplam600dan690a = Object.keys(balanceMap).reduce((s, k) => {
      const n = parseFloat(k);
      if (!Number.isNaN(n) && n >= 600 && n < 690) return s + (balanceMap[k] || 0);
      return s;
    }, 0);
    balanceMap['690'] = toplam600dan690a;
    const deger691 = balanceMap['691'] != null ? balanceMap['691'] : 0;
    balanceMap['692'] = toplam600dan690a + deger691;
  }
  const getVal = (code) => {
    if (code.length === 3) return balanceMap[code] != null ? balanceMap[code] : 0;
    if (code.length === 2) {
      return Object.keys(balanceMap).reduce(
        (s, k) => (k.length >= 3 && k.startsWith(code) ? s + (balanceMap[k] || 0) : s),
        0
      );
    }
    if (code.length === 1) {
      return Object.keys(balanceMap).reduce((s, k) => (k.startsWith(code) ? s + (balanceMap[k] || 0) : s), 0);
    }
    return 0;
  };
  return { balanceMap, getVal };
}

function buildGelirThpSonuc(sourceData, selectedPeriod, codeOrder) {
  const { balanceMap, getVal } = getThpGelirBalanceMapAndVal(sourceData, selectedPeriod);
  const balanceKeys = Object.keys(balanceMap);
  const hasData = (code) => {
    if (code.length === 3) return balanceMap[code] != null;
    if (code.length === 2) return balanceKeys.some((k) => k.length >= 3 && k.startsWith(code));
    if (code.length === 1) {
      return balanceKeys.some((k) => k.startsWith(code));
    }
    return false;
  };
  const sonuc = [];
  let index = 0;
  (codeOrder || []).forEach((t) => {
    if (!hasData(t.code)) return;
    sonuc.push({
      type: 'kalem',
      accountCode: t.code,
      accountName: t.name,
      bilancoDegeri: getVal(t.code),
      ilkHane: t.code.charAt(0),
      index: index++,
    });
  });
  return sonuc;
}

const TrialBalance = ({ embedded = false }) => {
  const { taxId, selectedCompany, companyId } = useCompany();
  const { t } = useLanguage();
  const { darkMode } = useAppTheme();
  const uploadFileInputRef = useRef(null);
  const selectedIdentifier = taxId;
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [mizanData, setMizanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'period'
  const [companyInfo, setCompanyInfo] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Açık olan account code'lar
  const [pdfData, setPdfData] = useState(null); // PDF'ten okunan veriler
  const [pdfFile, setPdfFile] = useState(null); // Yüklenen PDF dosyası (görüntüleme için)
  const [pdfFileName, setPdfFileName] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false); // PDF yükleme durumu
  const [activeTab, setActiveTab] = useState(0); // 0 Table, 1 Bilanço (period)
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [trialBalanceSourceTab, setTrialBalanceSourceTab] = useState(0); // 0: General Journal, 1: File
  const currentUser = AuthService.getCurrentUser();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadedMizansRefreshToken, setUploadedMizansRefreshToken] = useState(0);
  const [deletingPdf, setDeletingPdf] = useState(false); // PDF silme durumu
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Silme onay dialog'u
  const [pdfStorageInfo, setPdfStorageInfo] = useState(null); // PDF saklama bilgisi
  const [missingMizanDocuments, setMissingMizanDocuments] = useState([]); // Mizan oluşturulmayan belgeler
  const [showMissingMizanList, setShowMissingMizanList] = useState(false); // Eksik mizan listesini göster/gizle
  const [calculatingMizanForDoc, setCalculatingMizanForDoc] = useState(new Set()); // Hangi belgeler için hesaplama yapılıyor
  const [selectedYear, setSelectedYear] = useState(null); // Seçili yıl
  const bilancoExportRef = useRef(null); // Bilanço tab export data (set when Bilanço tab renders)
  const [tdhNameByCode, setTdhNameByCode] = useState({}); // Backend TDH sözlüğü (code -> name)
  const [tdhList, setTdhList] = useState([]); // Backend TDH listesi (sıralama/hiyerarşi için)
  // 4. dönem bilanço: mizan yerine kapanış ayı son 5 fişinden türetilen veri
  const [period4BilancoFromYevmiye, setPeriod4BilancoFromYevmiye] = useState(null);
  const [period4BilancoLoading, setPeriod4BilancoLoading] = useState(false);
  const [period4BilancoError, setPeriod4BilancoError] = useState(null);

  const resetUploadModalState = () => {
    setUploadSelectedFile(null);
    setUploadLoading(false);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);
  };

  const openUploadModal = () => {
    setUploadDialogOpen(true);
    // Dialog açıkken state'i sıfırlamak daha az "takılma" yapıyor.
    setTimeout(() => resetUploadModalState(), 0);
  };

  const closeUploadModal = () => {
    setUploadDialogOpen(false);
    // Transition bitene yakın state sıfırlama (UI'nin geç kapanmasını azaltır).
    setTimeout(() => resetUploadModalState(), 250);
  };

  const handleUploadFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls', '.pdf'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      setUploadError('Please select an Excel (.xlsx, .xls) or PDF (.pdf) file.');
      setUploadSuccess(false);
      return;
    }

    setUploadSelectedFile(file);
    setUploadError(null);
    setUploadSuccess(false);
  };

  const pollUploadJobStatus = async (jobId) => {
    const maxAttempts = 300; // ~5 dakika
    let attempts = 0;
    let currentProgress = 30;

    return new Promise((resolve) => {
      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/api/mizan/upload/${jobId}/status`);
          if (!response.ok) return;

          const statusData = await response.json();
          const status = statusData?.status;

          if (status === 'PROCESSING') {
            currentProgress = Math.min(30 + Math.round((attempts / maxAttempts) * 60), 95);
            setUploadProgress(currentProgress);
            return;
          }

          if (status === 'COMPLETED') {
            clearInterval(pollInterval);
            setUploadProgress(100);
            resolve(true);
            return;
          }

          if (status === 'FAILED') {
            clearInterval(pollInterval);
            const errMsg = statusData?.errorMessage || 'Mizan işleme başarısız oldu.';
            setUploadError(errMsg);
            resolve(false);
          }
        } catch (err) {
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setUploadError('Mizan işleme zaman aşımına uğradı. Lütfen tekrar deneyin.');
            resolve(false);
          }
        }

        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setUploadError('Mizan işleme zaman aşımına uğradı. Lütfen tekrar deneyin.');
          resolve(false);
        }
      }, 1000);
    });
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!uploadSelectedFile) {
      setUploadError('Please select a file.');
      return;
    }
    if (!companyId || !taxId) {
      setUploadError('Please enter Company ID and Tax Number.');
      return;
    }
    if (!currentUser?.id) {
      setUploadError('User information not found. Please login again.');
      return;
    }

    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);

    try {
      const xhr = new XMLHttpRequest();
      let uploadProgressValue = 0;

      const uploadResult = await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            uploadProgressValue = Math.round((e.loaded / e.total) * 30);
            setUploadProgress(uploadProgressValue);
          }
        });

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              resolve(responseData);
            } catch {
              resolve(xhr.responseText);
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || errorData || 'Upload failed'));
            } catch {
              reject(new Error(xhr.statusText || 'Upload failed'));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        const formData = new FormData();
        formData.append('file', uploadSelectedFile);
        formData.append('userId', currentUser.id);
        formData.append('companyId', companyId);
        formData.append('taxId', taxId);

        xhr.open('POST', `${EDEFTER_API_URL}/api/mizan/upload`);
        const token = AuthService.getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        xhr.send(formData);
      });

      let ok = false;

      if (uploadResult?.jobId) {
        setUploadProgress(30);
        ok = await pollUploadJobStatus(uploadResult.jobId);
      } else if (uploadResult?.mizanList) {
        ok = true;
      } else {
        setUploadError('Unexpected response format.');
      }

      if (ok) {
        setUploadSuccess(true);
        setTimeout(() => {
          setUploadedMizansRefreshToken((t) => t + 1);
          closeUploadModal();
        }, 900);
      } else {
        setUploadProgress(0);
      }
    } catch (err) {
      setUploadError(err?.message || 'An error occurred while uploading mizan.');
      setUploadProgress(0);
    } finally {
      setUploadLoading(false);
      setUploadProgress((p) => (p === 100 ? 100 : 0));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchTdhList = async () => {
      try {
        const res = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/tdh-list`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !Array.isArray(data)) return;
        setTdhList(data);
        const next = {};
        data.forEach((item) => {
          const code = String(item?.code || '').trim();
          const name = String(item?.name || '').trim();
          if (!code || !name) return;
          next[code] = name;
        });
        setTdhNameByCode(next);
      } catch (e) {
        // TDH endpoint erişilemezse sabit THP listeleri fallback olarak kullanılacak.
      }
    };
    fetchTdhList();
    return () => { cancelled = true; };
  }, []);

  // Mevcut belgelerden yılları çıkar
   const availableYears = useMemo(() => {
    if (!documents || documents.length === 0) return [];
    
    const yearsSet = new Set();
    documents.forEach(doc => {
      const dateStr = doc.periodCoveredStart || doc.periodCoveredEnd;
      if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          yearsSet.add(date.getFullYear());
        }
      }
    });
    
    // Yılları azalan sırada sırala (en yeni önce)
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [documents]);

  // Seçili yıla göre filtrelenmiş belgeler
  const filteredDocuments = useMemo(() => {
    if (!selectedYear) return documents;
    
    return documents.filter(doc => {
      const dateStr = doc.periodCoveredStart || doc.periodCoveredEnd;
       if (dateStr) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.getFullYear() === selectedYear;
        }
      }
      return false;
    });
  }, [documents, selectedYear]);

  // Yıl değiştiğinde selectedYear'i otomatik ayarla (ilk yıl)
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]); // En yeni yılı varsayılan olarak seç
    }
  }, [availableYears, selectedYear]);

  // Seçili belgenin filtrelenmiş belgeler içinde olup olmadığını kontrol et
  useEffect(() => {
    if (selectedDocument && selectedYear && filteredDocuments.length > 0) {
      const isDocumentInFiltered = filteredDocuments.some(doc => {
        if (doc.id && selectedDocument.id) {
          return doc.id === selectedDocument.id;
        }
        return doc.uniqueId === selectedDocument.uniqueId && 
               doc.periodCoveredStart === selectedDocument.periodCoveredStart &&
               doc.periodCoveredEnd === selectedDocument.periodCoveredEnd;
      });
      
      if (!isDocumentInFiltered) {
        // Seçili belge filtrelenmiş belgeler içinde değilse temizle
        setSelectedDocument(null);
        setMizanData(null);
      }
    }
  }, [selectedYear, filteredDocuments, selectedDocument]);

  // Mali yıl aralığı: organization.fiscalYearStart / fiscalYearEnd.
  // Kapanış ayı fiscalYearEnd'den gelir.
  const fiscalYearRangeForYear = useMemo(() => {
    if (!documents?.length || !selectedYear) return null;
    const inYear = documents.filter((doc) => {
      const s = doc?.fiscalYearStart ? new Date(doc.fiscalYearStart) : null;
      const e = doc?.fiscalYearEnd ? new Date(doc.fiscalYearEnd) : null;
      if (!s || !e || isNaN(s.getTime()) || isNaN(e.getTime())) return false;
      return s.getFullYear() === selectedYear || e.getFullYear() === selectedYear;
    });
    if (inYear.length === 0) return null;

    const normalized = inYear
      .map((doc) => {
        const start = new Date(doc.fiscalYearStart);
        const end = new Date(doc.fiscalYearEnd);
        return { start, end };
      })
      .filter((x) => !isNaN(x.start.getTime()) && !isNaN(x.end.getTime()) && x.start <= x.end)
      .sort((a, b) => a.start - b.start);

    return normalized.length > 0 ? normalized[0] : null;
  }, [documents, selectedYear]);

  // Açılış ayı: has_opening_entry=true olan belgenin periodCoveredStart ayı.
  // has_opening_entry artık backend'de fiscalYearStart/periodCoveredStart eşleşmesiyle set edilir.
  const openingMonthForYear = useMemo(() => {
    if (!documents?.length || !selectedYear) return null;
    const openingDocs = documents.filter((doc) => doc.hasOpeningEntry && doc.periodCoveredStart);
    const months = openingDocs
      .map((doc) => new Date(doc.periodCoveredStart))
      .filter((d) => !isNaN(d.getTime()) && d.getFullYear() === selectedYear)
      .map((d) => d.getMonth() + 1);
    if (months.length === 0) return null;
    return Math.min(...months);
  }, [documents, selectedYear]);

  // Ay adları (etiket için)
  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const pad = (n) => String(n).padStart(2, '0');
  const lastDayOfMonth = (y, m) => new Date(y, m, 0); // m 1-based month

  // Dönem seçenekleri (kümülatif) - açılış ayı has_opening_entry'den gelir.
  // 4. dönem bitişi doğrudan fiscalYearEnd'dir.
  const periods = useMemo(() => {
    const fallbackYear = selectedYear || new Date().getFullYear();
    const fiscalStartDate = fiscalYearRangeForYear?.start ? new Date(fiscalYearRangeForYear.start) : new Date(fallbackYear, 0, 1);
    const fyEnd = fiscalYearRangeForYear?.end ? new Date(fiscalYearRangeForYear.end) : new Date(fallbackYear, 11, 31);
    const startMonth = openingMonthForYear || (fiscalStartDate.getMonth() + 1);
    const fyStart = new Date(fiscalStartDate.getFullYear(), startMonth - 1, 1);
    const start = `${fyStart.getFullYear()}-${pad(fyStart.getMonth() + 1)}-${pad(fyStart.getDate())}`;

    // 3/6/9. dönemler ay sonuna gider; 4. dönem bitişi fiscalYearEnd.
    const end1 = lastDayOfMonth(fyStart.getFullYear(), fyStart.getMonth() + 1 + 2);
    const end2 = lastDayOfMonth(fyStart.getFullYear(), fyStart.getMonth() + 1 + 5);
    const end3 = lastDayOfMonth(fyStart.getFullYear(), fyStart.getMonth() + 1 + 8);
    const end4 = fyEnd;

    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const name = (m) => monthNames[m - 1] || '';
    const end4MonthName = name(end4.getMonth() + 1);

    return [
      { value: '1', label: `1. Dönem (${name(startMonth)}-${name(startMonth + 2)} - 3 Ay)`, start, end: fmt(end1) },
      { value: '2', label: `2. Dönem (${name(startMonth)}-${name(startMonth + 5)} - 6 Ay)`, start, end: fmt(end2) },
      { value: '3', label: `3. Dönem (${name(startMonth)}-${name(startMonth + 8)} - 9 Ay)`, start, end: fmt(end3) },
      { value: '4', label: `4. Dönem (${name(startMonth)}-${end4MonthName} - 12 Ay)`, start, end: fmt(end4) },
    ];
  }, [selectedYear, fiscalYearRangeForYear, openingMonthForYear]);

  // 4. dönem kapanış ayı belgesi: fiscalYearEnd ayına denk gelen belge.
  const closingMonthDocument = useMemo(() => {
    if (!filteredDocuments?.length || !fiscalYearRangeForYear?.end) return null;
    const fyEnd = new Date(fiscalYearRangeForYear.end);
    if (isNaN(fyEnd.getTime())) return null;
    const closingYearMonth = `${fyEnd.getFullYear()}-${pad(fyEnd.getMonth() + 1)}`;
    if (closingYearMonth.length < 7) return null;
    return filteredDocuments.find(doc => {
      const start = String(doc?.periodCoveredStart || '');
      return start.substring(0, 7) === closingYearMonth;
    }) || null;
  }, [filteredDocuments, fiscalYearRangeForYear]);

  // TaxId değiştiğinde state'leri temizle
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setSelectedPeriod(null);
      setMizanData(null);
      setCompanyInfo(null);
      setError(null);
      setSelectedYear(null);
      return;
    }
  }, [selectedIdentifier]);

  // Fetch documents when identifier is selected
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setMizanData(null);
      setCompanyInfo(null);
      return;
    }

    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/documents-by-identifier?identifier=${selectedIdentifier}`);
        if (response.ok) {
          const data = await response.json();
          // Sort documents: by year (descending - 2025, 2024, ...), then by month (1-12)
          const sortedDocuments = (data || []).sort((a, b) => {
            const dateA = new Date(a.periodCoveredStart);
            const dateB = new Date(b.periodCoveredStart);
            
            // Get year and month from period start date
            const yearA = dateA.getFullYear();
            const monthA = dateA.getMonth() + 1; // getMonth() returns 0-11, so add 1
            const yearB = dateB.getFullYear();
            const monthB = dateB.getMonth() + 1;
            
            // First sort by year (descending - newer years first: 2025, 2024, ...)
            if (yearB !== yearA) {
              return yearB - yearA;
            }
            
            // Then sort by month (ascending - 1 to 12)
            return monthA - monthB;
          });
          
          setDocuments(sortedDocuments);
          
          // Identifier'dan direkt companyInfo'yu al
          try {
            const companyInfoResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/company-info-by-identifier?identifier=${selectedIdentifier}`);
            if (companyInfoResponse.ok) {
              const companyInfoData = await companyInfoResponse.json();
              if (companyInfoData) {
                setCompanyInfo(companyInfoData);
              }
            }
          } catch (err) {
            console.error('Error fetching company info:', err);
          }
        } else {
          setError('Failed to load documents');
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [selectedIdentifier]);

  // Fetch mizan when document is selected (single month view)
  useEffect(() => {
    if (viewMode !== 'single' || !selectedDocument) {
      if (viewMode === 'single') {
        setMizanData(null);
      }
      return;
    }

    // Check if document has an id
    if (!selectedDocument.id) {
      console.error('Selected document does not have an id:', selectedDocument);
      setError('Seçilen belge geçersiz: ID bulunamadı');
      setMizanData(null);
      return;
    }

    const fetchMizan = async () => {
      try {
        setLoading(true);
        setError(null);
        const url = `${EDEFTER_API_URL}/mizan-by-document?documentId=${selectedDocument.id}`;
        const response = await AuthService.authenticatedFetch(url);
        if (response.ok) {
          const data = await response.json();
          // Eğer mizan verisi boşsa, otomatik olarak hesaplamayı dene
          if (!data.rows || data.rows.length === 0) {
            try {
              const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${selectedDocument.id}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
              });
              
              if (calculateResponse.ok) {
                const calculateResult = await calculateResponse.json();
            
                // Hesaplama başarılıysa, mizan verisini tekrar çek
                if (calculateResult.success) {
                 const retryResponse = await AuthService.authenticatedFetch(url);
                  if (retryResponse.ok) {
                    const retryData = await retryResponse.json();
                  
                    if (retryData.rows && retryData.rows.length > 0) {
                      setMizanData(retryData);
                      setError(null);
                    } else {
                      // Hesaplama yapıldı ama hala veri yok
                      setMizanData(retryData);
                      setError('Trial balance was calculated but no rows were found for this document. The document may be empty or entry details may be missing.');
                    }
                  } else {
                    setMizanData(data);
                    setError('Trial balance was calculated but an error occurred while loading data.');
                  }
                } else {
                  console.warn('Mizan calculation failed:', calculateResult.error);
                  setMizanData(data);
                  setError(calculateResult.error || 'Failed to calculate trial balance. Please try manually.');
                }
              } else {
                const errorText = await calculateResponse.text();
                console.warn('Failed to calculate mizan:', errorText);
                setMizanData(data);
                setError(`Failed to calculate trial balance: ${errorText || 'Unknown error'}. Please try manually.`);
              }
            } catch (calcErr) {
              console.error('Error calculating mizan:', calcErr);
              setMizanData(data);
              setError(`Error calculating trial balance: ${calcErr.message}. Please try manually.`);
            }
          } else {
            setMizanData(data);
            setError(null); // Veri varsa hata yok
          }
        } else {
          const errorText = await response.text();
          console.error('Failed to load mizan:', {
            status: response.status,
            statusText: response.statusText,
            error: errorText
          });
          setError(`Mizan yüklenemedi: ${response.status} ${response.statusText}`);
        }
      } catch (err) {
        console.error('Error fetching mizan:', err);
        setError(`Mizan yüklenirken hata oluştu: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchMizan();
  }, [selectedDocument, viewMode]);

  // Fetch aggregated mizan when period is selected
  useEffect(() => {
    if (viewMode !== 'period' || !selectedIdentifier || !selectedPeriod || !selectedYear) {
      if (viewMode === 'period') {
        setMizanData(null);
      }
      return;
    }

    const fetchPeriodMizan = async () => {
      try {
        setLoading(true);
        setError(null);
        const period = periods.find(p => p.value === selectedPeriod);
        if (!period) {
          setError('Period not found');
          return;
        }
                
        const response = await AuthService.authenticatedFetch(
          `${EDEFTER_API_URL}/mizan-by-period?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`
        );
        if (response.ok) {
          const data = await response.json();         
          
          // Eğer period mizan verisi boşsa, period içindeki belgeler için mizan hesaplamayı dene
          if (!data.rows || data.rows.length === 0) {
            
           // Period içindeki belgeleri bul (filtrelenmiş belgelerden)
           const periodStartDate = new Date(period.start);
            const periodEndDate = new Date(period.end);
            const periodDocuments = filteredDocuments.filter(doc => {
              if (!doc.periodCoveredStart || !doc.periodCoveredEnd) return false;
              const docStart = new Date(doc.periodCoveredStart);
              const docEnd = new Date(doc.periodCoveredEnd);
              // Belge period içinde mi kontrol et
              return docStart <= periodEndDate && docEnd >= periodStartDate;
            });
            
            if (periodDocuments.length > 0) {
              
              // Eksik mizan olan belgeleri topla
              const missingMizanDocs = [];
              
              // Her belge için mizan var mı kontrol et, yoksa ve entry varsa listeye ekle
              for (const doc of periodDocuments) {
                if (doc.id) {
                  try {
                    // Önce bu belge için mizan var mı kontrol et
                    const mizanCheckResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${doc.id}`);
                    if (mizanCheckResponse.ok) {
                      const mizanCheckData = await mizanCheckResponse.json();
                      // Eğer mizan yoksa, belgede entry var mı kontrol et
                      if (!mizanCheckData.rows || mizanCheckData.rows.length === 0) {
                        // Belgede entry var mı kontrol et (sadece ilk sayfayı kontrol et, entry varsa yeterli)
                        const entryCheckResponse = await AuthService.authenticatedFetch(
                          `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${doc.id}&page=0&size=1`
                        );
                        
                        if (entryCheckResponse.ok) {
                          const entryCheckData = await entryCheckResponse.json();
                          // Eğer entry varsa, eksik mizan listesine ekle
                          if (entryCheckData.entryHeaders && entryCheckData.entryHeaders.length > 0) {
                            missingMizanDocs.push({
                              ...doc,
                              entryCount: entryCheckData.totalElements || 0
                            });
                          }
                        }
                      }
                    }
                  } catch (checkErr) {
                    console.error(`Error checking document ${doc.id}:`, checkErr);
                  }
                }
              }
              
              // Eksik mizan belgelerini state'e kaydet
              if (missingMizanDocs.length > 0) {
                setMissingMizanDocuments(missingMizanDocs);
                setShowMissingMizanList(true);
              } else {
                setMissingMizanDocuments([]);
                setShowMissingMizanList(false);
              }
              
              // Veri yoksa hata mesajı göster
              setMizanData(data);
              if (missingMizanDocs.length > 0) {
                setError(null); // Alert'te gösterilecek, burada hata gösterme
              } else {
                setError('No trial balance data found for this period. No documents with entries found in this period.');
              }
            } else {
              setMizanData(data);
              setError('No documents found for the selected period.');
            }
          } else {
            // Veri varsa bile eksik mizan kontrolü yap (bazı belgeler için mizan olmayabilir)
            setMizanData(data);
            setError(null);
            
           // Period içindeki belgeleri kontrol et, eksik mizan var mı? (filtrelenmiş belgelerden)
           const periodStartDate = new Date(period.start);
            const periodEndDate = new Date(period.end);
            const periodDocuments = filteredDocuments.filter(doc => {
              if (!doc.periodCoveredStart || !doc.periodCoveredEnd) return false;
              const docStart = new Date(doc.periodCoveredStart);
              const docEnd = new Date(doc.periodCoveredEnd);
              return docStart <= periodEndDate && docEnd >= periodStartDate;
            });
            
            if (periodDocuments.length > 0) {
              // Eksik mizan olan belgeleri kontrol et (async olarak)
              (async () => {
                const missingMizanDocs = [];
                
                for (const doc of periodDocuments) {
                  if (doc.id) {
                    try {
                      const mizanCheckResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${doc.id}`);
                      if (mizanCheckResponse.ok) {
                        const mizanCheckData = await mizanCheckResponse.json();
                        if (!mizanCheckData.rows || mizanCheckData.rows.length === 0) {
                          const entryCheckResponse = await AuthService.authenticatedFetch(
                            `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${doc.id}&page=0&size=1`
                          );
                          
                          if (entryCheckResponse.ok) {
                            const entryCheckData = await entryCheckResponse.json();
                            if (entryCheckData.entryHeaders && entryCheckData.entryHeaders.length > 0) {
                              missingMizanDocs.push({
                                ...doc,
                                entryCount: entryCheckData.totalElements || 0
                              });
                            }
                          }
                        }
                      }
                    } catch (checkErr) {
                      console.error(`Error checking document ${doc.id}:`, checkErr);
                    }
                  }
                }
                
                if (missingMizanDocs.length > 0) {
                  setMissingMizanDocuments(missingMizanDocs);
                  setShowMissingMizanList(true);
                } else {
                  setMissingMizanDocuments([]);
                  setShowMissingMizanList(false);
                }
              })();
            }
          }
          
          // Kaydedilmiş PDF'i yükle (hem dosya hem de veri)
          if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
            const period = periods.find(p => p.value === selectedPeriod);
            if (period) {
              try {
                const pdfResponse = await AuthService.authenticatedFetch(
                  `${EDEFTER_API_URL}/getPdf?identifier=${encodeURIComponent(selectedIdentifier)}&periodStart=${encodeURIComponent(period.start)}&periodEnd=${encodeURIComponent(period.end)}`
                );
                if (pdfResponse.ok) {
                  const blob = await pdfResponse.blob();
                  const fileUrl = URL.createObjectURL(blob);
                  // Eski URL'i temizle
                  if (pdfFile) {
                    URL.revokeObjectURL(pdfFile);
                  }
                  setPdfFile(fileUrl);
                  setPdfFileName(
                    extractPdfFileName(pdfResponse, selectedIdentifier, period.start, period.end)
                  );
                  
                  // PDF verisini de yükle (tekrar okutarak)
                  const formData = new FormData();
                  formData.append('pdfData', blob, 'saved.pdf');
                  formData.append('identifier', selectedIdentifier);
                  formData.append('periodStart', period.start);
                  formData.append('periodEnd', period.end);
                  
                  const pdfDataResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/readVergiPDF`, {
                    method: 'POST',
                    body: formData,
                  });
                  
                  if (pdfDataResponse.ok) {
                    const pdfDataResult = await pdfDataResponse.json();
                    setPdfData(pdfDataResult);
                  } else {
                    const errorText = await pdfDataResponse.text();
                    console.warn('⚠️ Failed to load saved PDF data:', errorText);
                    setPdfData(null);
                  }
                } else {
                  // PDF bulunamadıysa, mevcut PDF'i temizle (yeni period için PDF yok)
                  if (pdfFile) {
                    URL.revokeObjectURL(pdfFile);
                  }
                  setPdfFile(null);
                  setPdfFileName(null);
                  setPdfData(null);
                }
              } catch (pdfErr) {
                console.error('❌ Error loading saved PDF:', pdfErr);
                // Hata durumunda mevcut PDF'i temizle
                if (pdfFile) {
                  URL.revokeObjectURL(pdfFile);
                }
                setPdfFile(null);
                setPdfFileName(null);
                setPdfData(null);
              }
            }
          }
        } else {
          const errorText = await response.text();
          console.error('Failed to load period mizan:', errorText);
          setError('Failed to load period mizan: ' + errorText);
        }
      } catch (err) {
        console.error('Error fetching period mizan:', err);
        setError('Failed to load period mizan: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPeriodMizan();
  }, [selectedPeriod, selectedIdentifier, viewMode, periods, filteredDocuments]);

  // 4. dönem: kapanış ayı belgesinden bilanço verisi türet (mizan yerine)
  useEffect(() => {
    if (viewMode !== 'period' || selectedPeriod !== '4') {
      setPeriod4BilancoFromYevmiye(null);
      setPeriod4BilancoError(null);
      return;
    }
    if (periods?.length && filteredDocuments?.length && !closingMonthDocument) {
      setPeriod4BilancoFromYevmiye(null);
      setPeriod4BilancoError('Kapanış ayı belgesi bulunamadı. 4. dönemin son ayına ait general journal belgesi yüklenmeli.');
      setPeriod4BilancoLoading(false);
      return;
    }
    if (!closingMonthDocument?.id) {
      return;
    }
    let cancelled = false;
    const docId = closingMonthDocument.id;
    setPeriod4BilancoLoading(true);
    setPeriod4BilancoError(null);
    (async () => {
      try {
        const period4 = periods?.find((p) => p.value === '4');
        const lastDayStr = String(period4?.end || String(closingMonthDocument?.periodCoveredEnd || '').substring(0, 10) || '').trim();
        if (!lastDayStr || lastDayStr.length < 10) {
          setPeriod4BilancoError('Kapanış ayının son günü belirlenemedi.');
          setPeriod4BilancoFromYevmiye(null);
          return;
        }

        // Aktif/Pasif (1–5xx): kapanış ayı fişlerinde entryNumberCounter'ı en büyük olan fiş
        const maxCounterRes = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/journal-entry-by-document-max-counter?documentId=${docId}`);
        if (cancelled) return;
        if (!maxCounterRes.ok) {
          setPeriod4BilancoError('Fiş (max counter) alınamadı.');
          setPeriod4BilancoFromYevmiye(null);
          return;
        }
        const maxCounterData = await maxCounterRes.json();
        if (maxCounterData?.error) {
          setPeriod4BilancoError(maxCounterData.error || 'Fiş (max counter) alınamadı.');
          setPeriod4BilancoFromYevmiye(null);
          return;
        }
        const maxCounterHeaders = maxCounterData?.entryHeaders || [];
        if (maxCounterHeaders.length === 0) {
          setPeriod4BilancoFromYevmiye([]);
          setPeriod4BilancoError(null);
          return;
        }
        const sonFisId = maxCounterHeaders[0]?.id;
        if (!sonFisId) {
          setPeriod4BilancoFromYevmiye([]);
          setPeriod4BilancoError(null);
          return;
        }
        const batchRes = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/journal-entry-details-batch?entryHeaderIds=${sonFisId}`);
        if (cancelled) return;
        if (!batchRes.ok) {
          setPeriod4BilancoError('Fiş detayları alınamadı.');
          setPeriod4BilancoFromYevmiye(null);
          return;
        }
        const batchData = await batchRes.json();
        const batchArr = Array.isArray(batchData) ? batchData : [];
        const lastItem = batchArr[0];
        const lastDetails = lastItem?.entryDetails ?? lastItem?.entryHeader?.entryDetails ?? [];

        // Gelir tablosu (6xx): kapanış ayının son gününde, içinde 690 olan fişlerden sadece 6xx alınır (7xx yok)
        const incomeRes = await AuthService.authenticatedFetch(
          `${EDEFTER_API_URL}/journal-income-6xx-by-document-date-690?documentId=${docId}&date=${encodeURIComponent(lastDayStr)}`
        );
        if (cancelled) return;
        if (!incomeRes.ok) {
          setPeriod4BilancoError('Gelir tablosu (6xx) verisi alınamadı.');
          setPeriod4BilancoFromYevmiye(null);
          return;
        }
        const incomeData = await incomeRes.json();

        const byAccountAktifPasif = new Map();

        const addToMap = (map, mainId, mainDescription, amount, isDebit) => {
          if (!mainId) return;
          const key = String(mainId).trim();
          const num = parseFloat(amount) || 0;
          if (!map.has(key)) {
            map.set(key, { accountCode: key, mainDescription: mainDescription || '', totalBorc: 0, totalAlacak: 0 });
          }
          const row = map.get(key);
          if (isDebit) row.totalBorc += num; else row.totalAlacak += num;
        };

        lastDetails.forEach((detail) => {
          const amount = detail?.amount != null ? parseFloat(detail.amount) : 0;
          const dc = (detail?.debitCreditCode || '').toUpperCase();
          const isDebit = dc === 'D' || dc === 'DEBIT' || dc === 'B' || String(dc).startsWith('D');
          (detail?.accounts || []).forEach((acc) => {
            const mainId = acc?.mainId;
            const mainDescription = acc?.mainDescription;
            const codeStr = String(mainId || '').trim();
            const ilkHane = codeStr.charAt(0);
            if (ilkHane === '1' || ilkHane === '2' || ilkHane === '3' || ilkHane === '4' || ilkHane === '5') {
              addToMap(byAccountAktifPasif, mainId, mainDescription, amount, isDebit);
            }
          });
        });

        const rows = [];
        const pushFromMap = (map) => {
          map.forEach((row, code) => {
            const borc = row.totalBorc || 0;
            const alacak = row.totalAlacak || 0;
            const fark = borc - alacak;
            const borcBakiyesi = fark > 0 ? fark : 0;
            const alacakBakiyesi = fark < 0 ? -fark : 0;
            rows.push({
              accountCode: code,
              mainDescription: row.mainDescription,
              totalBorc: borc,
              totalAlacak: alacak,
              totalBorcBakiyesi: borcBakiyesi,
              totalAlacakBakiyesi: alacakBakiyesi,
            });
          });
        };

        // 1–5xx (Aktif/Pasif) sadece son fişten
        pushFromMap(byAccountAktifPasif);

        // 6xx (Gelir Tablosu) kapanış ayının son günündeki 690 içeren fişlerden (backend aggregate)
        (Array.isArray(incomeData) ? incomeData : []).forEach((r) => {
          const code = String(r?.accountCode || '').trim();
          if (!code) return;
          rows.push({
            accountCode: code,
            mainDescription: r?.mainDescription || '',
            totalBorc: parseFloat(r?.totalBorc) || 0,
            totalAlacak: parseFloat(r?.totalAlacak) || 0,
            totalBorcBakiyesi: parseFloat(r?.totalBorcBakiyesi) || 0,
            totalAlacakBakiyesi: parseFloat(r?.totalAlacakBakiyesi) || 0,
          });
        });

        rows.sort((a, b) => (a.accountCode || '').localeCompare(b.accountCode || ''));
        if (!cancelled) {
          setPeriod4BilancoFromYevmiye(rows);
          setPeriod4BilancoError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setPeriod4BilancoError(err?.message || 'Yevmiye verisi yüklenemedi.');
          setPeriod4BilancoFromYevmiye(null);
        }
      } finally {
        if (!cancelled) setPeriod4BilancoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [viewMode, selectedPeriod, closingMonthDocument, periods, filteredDocuments]);

  // Identifier artık CompanyContext'ten geliyor, bu handler artık gerekli değil
  // Ancak identifier değiştiğinde state'leri temizlemek için useEffect kullanıyoruz

  const handleDocumentChange = (event, newValue, reason) => {
    if (reason === 'selectOption') {
      setSelectedDocument(newValue);
      setSelectedPeriod(null);
      setViewMode('single');
      setError(null);
      setMizanData(null); // Clear previous data when selecting new document
    } else if (reason === 'clear') {
      setSelectedDocument(null);
      setMizanData(null);
      setError(null);
    }
  };

  const handlePeriodChange = (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue) {
      // Period değiştiğinde eski verileri temizle
      setMizanData(null);
      setPdfData(null);
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
      setPdfFile(null);
      setPdfFileName(null);
      setSelectedPeriod(newValue.value);
      setSelectedDocument(null);
      setViewMode('period');
      setError(null);
      setMissingMizanDocuments([]);
      setShowMissingMizanList(false);
      if (newValue.value !== '4') {
        setPeriod4BilancoFromYevmiye(null);
        setPeriod4BilancoError(null);
      }
    } else if (reason === 'clear') {
      setSelectedPeriod(null);
      setMizanData(null);
      setPdfData(null);
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
      setPdfFile(null);
      setPdfFileName(null);
      setError(null);
      setMissingMizanDocuments([]);
      setShowMissingMizanList(false);
      setPeriod4BilancoFromYevmiye(null);
      setPeriod4BilancoError(null);
    }
  };

  const handleViewModeChange = (event) => {
    const mode = event.target.value;
    setViewMode(mode);
    if (mode === 'single') {
      setSelectedPeriod(null);
      setMissingMizanDocuments([]);
      setShowMissingMizanList(false);
    } else {
      setSelectedDocument(null);
    }
    setMizanData(null);
  };

  // Tek bir belge için mizan hesapla
  const handleCalculateMizanForDocument = async (docId) => {
    setCalculatingMizanForDoc(prev => new Set(prev).add(docId));
    try {
      const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${docId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (calculateResponse.ok) {
        const calculateResult = await calculateResponse.json();
        if (calculateResult.success) {
          // Başarılı hesaplama sonrası, belgeyi listeden çıkar
          setMissingMizanDocuments(prev => prev.filter(doc => doc.id !== docId));
          
          // Eğer tüm belgeler hesaplandıysa, period mizan verisini tekrar çek
          const remainingDocs = missingMizanDocuments.filter(doc => doc.id !== docId);
          if (remainingDocs.length === 0) {
            const period = periods.find(p => p.value === selectedPeriod);
            if (period) {
              await new Promise(resolve => setTimeout(resolve, 500));
              const retryResponse = await AuthService.authenticatedFetch(
                `${EDEFTER_API_URL}/mizan-by-period?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`
              );
              if (retryResponse.ok) {
                const retryData = await retryResponse.json();
                setMizanData(retryData);
                setShowMissingMizanList(false);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error(`Error calculating mizan for document ${docId}:`, err);
    } finally {
      setCalculatingMizanForDoc(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  // Tüm eksik mizan belgelerini hesapla
  const handleCalculateAllMissingMizans = async () => {
    if (missingMizanDocuments.length === 0) return;
    
    setLoading(true);
    try {
      const period = periods.find(p => p.value === selectedPeriod);
      if (!period) return;
      
      let calculatedCount = 0;
      for (const doc of missingMizanDocuments) {
        if (doc.id) {
          setCalculatingMizanForDoc(prev => new Set(prev).add(doc.id));
          try {
            const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${doc.id}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            
            if (calculateResponse.ok) {
              const calculateResult = await calculateResponse.json();
              if (calculateResult.success) {
                calculatedCount++;
              }
            }
          } catch (err) {
            console.error(`Error calculating mizan for document ${doc.id}:`, err);
          } finally {
            setCalculatingMizanForDoc(prev => {
              const newSet = new Set(prev);
              newSet.delete(doc.id);
              return newSet;
            });
          }
        }
      }
      
      // Tüm hesaplamalar tamamlandıktan sonra period mizan verisini tekrar çek
      if (calculatedCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const retryResponse = await AuthService.authenticatedFetch(
          `${EDEFTER_API_URL}/mizan-by-period?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`
        );
        if (retryResponse.ok) {
          const retryData = await retryResponse.json();
          setMizanData(retryData);
          setMissingMizanDocuments([]);
          setShowMissingMizanList(false);
        }
      }
    } catch (err) {
      console.error('Error calculating all missing mizans:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleDownloadExcel = () => {
    if (!groupedMizanData || groupedMizanData.length === 0) return;

    const toExcelNumber = (value) => {
      if (value === null || value === undefined) return 0;
      const n = parseFloat(value);
      return isNaN(n) ? 0 : Math.round(n * 100) / 100;
    };

    const formatTrDateSlash = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const periodStart = mizanData?.periodCoveredStart || selectedDocument?.periodCoveredStart || '';
    const periodEnd = mizanData?.periodCoveredEnd || selectedDocument?.periodCoveredEnd || '';
    const startDateStr = formatTrDateSlash(periodStart);
    const endDateStr = formatTrDateSlash(periodEnd);
    const companyTitle =
      selectedCompany?.name ||
      selectedCompany?.title ||
      selectedCompany?.companyName ||
      selectedCompany?.unvan ||
      companyInfo?.title ||
      companyInfo?.name ||
      '';

    const headerRow = [
      'HESAP KODU',
      'HESAP ADI',
      'BORÇ HAREKETLERİ TOPLAMI',
      'ALACAK HAREKETLERİ TOPLAMI',
      'BORÇ BAKİYESİ',
      'ALACAK BAKİYESİ',
    ];

    const rows = [
      ['MİZAN', '', '', '', '', ''],
      [companyTitle, '', '', '', '', ''],
      ['Dönem :', (startDateStr && endDateStr) ? `${startDateStr}-${endDateStr}` : '', '', '', '', ''],
      ['Tarih Aralığı :', (startDateStr && endDateStr) ? `${startDateStr}-${endDateStr}` : '', '', '', '', ''],
      ['', '', '', '', '', ''],
      headerRow,
    ];
    // Excel satır stilleri için (worksheet row index -> seviye)
    // 1: tek haneli, 2: iki haneli, 3: üç haneli, 4: alt kırılım
    const rowLevelMap = new Map();

    // İsim ve sıralama kaynağı: önce backend TDH, yoksa frontend THP fallback
    const thpNameMap = new Map();
    if (Array.isArray(tdhList) && tdhList.length > 0) {
      tdhList.forEach((item) => {
        const code = String(item?.code || '').trim();
        const name = String(item?.name || '').trim();
        if (!code || !name) return;
        thpNameMap.set(code, name);
      });
    } else {
      // Backend TDH yüklenmediyse yine de name map oluştur (en azından 3 haneliler mizan açıklamasından gelir).
      Object.entries(tdhNameByCode || {}).forEach(([code, name]) => {
        if (!code || !name) return;
        thpNameMap.set(code, name);
      });
    }
    const groupByBase3 = new Map();
    const totals3 = new Map();
    const totals2 = new Map();
    const totals1 = new Map();

    const addTotals = (targetMap, code, borc, alacak, borcBakiyesi, alacakBakiyesi) => {
      if (!code) return;
      if (!targetMap.has(code)) {
        targetMap.set(code, { borc: 0, alacak: 0, borcBakiyesi: 0, alacakBakiyesi: 0 });
      }
      const t = targetMap.get(code);
      t.borc += toExcelNumber(borc);
      t.alacak += toExcelNumber(alacak);
      t.borcBakiyesi += toExcelNumber(borcBakiyesi);
      t.alacakBakiyesi += toExcelNumber(alacakBakiyesi);
    };

    groupedMizanData.forEach((group) => {
      const raw = (group.accountCode || '').trim();
      if (!raw) return;
      const base = (raw.split('.')[0] || raw).trim();
      const base3 = base.length > 3 ? base.substring(0, 3) : base;
      if (!base3) return;

      addTotals(totals3, base3, group.totalBorc, group.totalAlacak, group.totalBorcBakiyesi, group.totalAlacakBakiyesi);

      if (!groupByBase3.has(base3)) groupByBase3.set(base3, group);
    });

    // 2 haneli hesaplar = aynı prefix'teki 3 hanelilerin toplamı (10 = 100+101+...)
    totals3.forEach((t3, code3) => {
      if (!code3 || code3.length < 2) return;
      addTotals(totals2, code3.substring(0, 2), t3.borc, t3.alacak, t3.borcBakiyesi, t3.alacakBakiyesi);
    });

    // 1 haneli hesaplar = aynı prefix'teki 2 hanelilerin toplamı (1 = 10+11+12+...)
    totals2.forEach((t2, code2) => {
      if (!code2) return;
      addTotals(totals1, code2.charAt(0), t2.borc, t2.alacak, t2.borcBakiyesi, t2.alacakBakiyesi);
    });

    const sortedBase3Codes = Array.from(groupByBase3.keys()).sort((a, b) => a.localeCompare(b, 'tr'));
    // 1 ve 2 haneli kodların sırası: TDH varsa onu baz al, yoksa mevcut sıralama
    const tdhCodes = Array.isArray(tdhList) ? tdhList.map((x) => String(x?.code || '').trim()).filter(Boolean) : [];
    const tdhLevel1Order = tdhCodes.filter((c) => c.length === 1);
    const fallbackLevel1 = Array.from(new Set(sortedBase3Codes.map((c) => c.charAt(0)))).sort((a, b) => a.localeCompare(b, 'tr'));
    const presentLevel1 = Array.from(totals1.keys());
    const level1Codes = (tdhLevel1Order.length > 0 ? tdhLevel1Order : fallbackLevel1).filter((c) => presentLevel1.includes(c));
    let genelToplamRowIndex = null;

    const pushCodeRow = (code, name, totals) => {
      rows.push([
        code,
        name || '',
        toExcelNumber(totals?.borc),
        toExcelNumber(totals?.alacak),
        toExcelNumber(totals?.borcBakiyesi),
        toExcelNumber(totals?.alacakBakiyesi),
      ]);
      const excelRowIndex = rows.length - 1;
      const level = code ? Math.min(String(code).length, 3) : 3;
      rowLevelMap.set(excelRowIndex, level);
    };

    level1Codes.forEach((lvl1) => {
      const lvl1Totals = totals1.get(lvl1);
      if (lvl1Totals) pushCodeRow(lvl1, thpNameMap.get(lvl1) || '', lvl1Totals);

      const tdhLevel2OrderForLvl1 = tdhCodes.filter((c) => c.length === 2 && c.startsWith(lvl1));
      const fallbackLvl2 = Array.from(
        new Set(
          sortedBase3Codes
            .filter((c) => c.startsWith(lvl1) && c.length >= 2)
            .map((c) => c.substring(0, 2))
        )
      ).sort((a, b) => a.localeCompare(b, 'tr'));
      const presentLvl2 = Array.from(totals2.keys()).filter((c) => c.startsWith(lvl1));
      const lvl2Codes = (tdhLevel2OrderForLvl1.length > 0 ? tdhLevel2OrderForLvl1 : fallbackLvl2).filter((c) => presentLvl2.includes(c));

      lvl2Codes.forEach((lvl2) => {
        const lvl2Totals = totals2.get(lvl2);
        if (lvl2Totals) pushCodeRow(lvl2, thpNameMap.get(lvl2) || '', lvl2Totals);

        const lvl3Codes = sortedBase3Codes.filter((c) => c.startsWith(lvl2) && c.length >= 3);
        lvl3Codes.forEach((lvl3) => {
          const group = groupByBase3.get(lvl3);
          if (!group) return;

          pushCodeRow(
            lvl3,
            group.mainDescription || thpNameMap.get(lvl3) || '',
            {
              borc: group.totalBorc,
              alacak: group.totalAlacak,
              borcBakiyesi: group.totalBorcBakiyesi,
              alacakBakiyesi: group.totalAlacakBakiyesi,
            }
          );

          if (Array.isArray(group.subRows) && group.subRows.length > 0) {
            group.subRows.forEach((sub) => {
              const subCode = (sub.subId || sub.hesapKodu || '').trim();
              if (!subCode || subCode === lvl3) return;
              rows.push([
                subCode,
                sub.subDescription || '',
                toExcelNumber(sub.borc),
                toExcelNumber(sub.alacak),
                toExcelNumber(sub.borcBakiyesi),
                toExcelNumber(sub.alacakBakiyesi),
              ]);
              rowLevelMap.set(rows.length - 1, 4);
            });
          }
        });
      });
    });

    // En alt satır: Genel Toplam (tekli hesaplar: 1,2,3... üzerinden)
    const genelToplam = level1Codes.reduce(
      (acc, code) => {
        const t = totals1.get(code);
        if (!t) return acc;
        acc.borc += toExcelNumber(t.borc);
        acc.alacak += toExcelNumber(t.alacak);
        acc.borcBakiyesi += toExcelNumber(t.borcBakiyesi);
        acc.alacakBakiyesi += toExcelNumber(t.alacakBakiyesi);
        return acc;
      },
      { borc: 0, alacak: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
    );
    rows.push([
      'GENEL TOPLAM:',
      '',
      toExcelNumber(genelToplam.borc),
      toExcelNumber(genelToplam.alacak),
      toExcelNumber(genelToplam.borcBakiyesi),
      toExcelNumber(genelToplam.alacakBakiyesi),
    ]);
    genelToplamRowIndex = rows.length - 1;

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // A1:F1 -> MİZAN
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }, // A2:F2 -> Şirket adı
      { s: { r: 2, c: 1 }, e: { r: 2, c: 5 } }, // B3:F3 -> Dönem değeri
      { s: { r: 3, c: 1 }, e: { r: 3, c: 5 } }, // B4:F4 -> Tarih Aralığı değeri
    ];
    if (genelToplamRowIndex != null) {
      ws['!merges'].push({ s: { r: genelToplamRowIndex, c: 0 }, e: { r: genelToplamRowIndex, c: 1 } }); // A:B -> GENEL TOPLAM
    }
    for (let c = 0; c <= 5; c++) {
      const ref1 = XLSX.utils.encode_cell({ r: 0, c });
      const ref2 = XLSX.utils.encode_cell({ r: 1, c });
      if (!ws[ref1]) ws[ref1] = { t: 's', v: '' };
      if (!ws[ref2]) ws[ref2] = { t: 's', v: '' };
      ws[ref1].s = { alignment: { horizontal: 'center', vertical: 'center' }, font: { bold: true } };
      ws[ref2].s = { alignment: { horizontal: 'center', vertical: 'center' }, font: { bold: true } };
    }
    const periodValueRef = XLSX.utils.encode_cell({ r: 2, c: 1 });
    const dateRangeValueRef = XLSX.utils.encode_cell({ r: 3, c: 1 });
    if (ws[periodValueRef]) {
      ws[periodValueRef].s = {
        ...(ws[periodValueRef].s || {}),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
    }
    if (ws[dateRangeValueRef]) {
      ws[dateRangeValueRef].s = {
        ...(ws[dateRangeValueRef].s || {}),
        alignment: { horizontal: 'left', vertical: 'center' },
      };
    }
    // Kolon başlık satırındaki hücreleri text sabitle
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r: 5, c });
      if (ws[ref]) {
        ws[ref].t = 's';
        ws[ref].z = '@';
        if (typeof ws[ref].v !== 'string') ws[ref].v = String(ws[ref].v ?? '');
        ws[ref].s = {
          ...(ws[ref].s || {}),
          font: { bold: true, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'A6A6A6' } },
          alignment: { horizontal: 'center', vertical: 'center' },
        };
      }
    }

    // Tüm dolu hücrelere daha belirgin border uygula
    const thickBorder = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } },
    };
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) continue;
        ws[ref].s = { ...(ws[ref].s || {}), border: thickBorder };
      }
    }
    [2, 3, 4, 5].forEach((col) => {
      for (let r = 6; r <= range.e.r; r++) {
        const ref = XLSX.utils.encode_cell({ r, c: col });
        if (ws[ref] && typeof ws[ref].v === 'number') {
          ws[ref].t = 'n';
          ws[ref].z = '#,##0.00';
        }
      }
    });

    // Kolon genişlikleri: Excel'de #### görünmesini azaltmak için sabit genişlik ver.
    ws['!cols'] = [
      { wch: 18 }, // HESAP KODU
      { wch: 40 }, // HESAP ADI
      { wch: 22 }, // BORÇ HAREKETLERİ TOPLAMI
      { wch: 22 }, // ALACAK HAREKETLERİ TOPLAMI
      { wch: 20 }, // BORÇ BAKİYESİ
      { wch: 20 }, // ALACAK BAKİYESİ
    ];

    // Kademeli satır stilleri: 1 haneli > 2 haneli > 3 haneli > alt kırılımlar
    for (let r = 6; r <= range.e.r; r++) {
      const level = rowLevelMap.get(r);
      if (!level) continue;

      let styleForLevel = null;
      if (level === 1) {
        styleForLevel = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'D9D9D9' } },
          alignment: { vertical: 'center' },
        };
      } else if (level === 2) {
        styleForLevel = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'EDEDED' } },
          alignment: { vertical: 'center' },
        };
      } else if (level === 3) {
        styleForLevel = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'F3F3F3' } },
          alignment: { vertical: 'center' },
        };
      } else if (level === 4) {
        styleForLevel = {
          font: { bold: false, color: { rgb: '333333' } },
          alignment: { vertical: 'center' },
        };
      }

      if (!styleForLevel) continue;
      for (let c = 0; c <= 5; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        if (!ws[ref]) continue;
        ws[ref].s = { ...(ws[ref].s || {}), ...styleForLevel };
      }
    }

    // Genel Toplam satırı: başlık satırıyla aynı stil (koyu arka plan, bold, border)
    if (genelToplamRowIndex != null) {
      for (let c = 0; c <= 5; c++) {
        const ref = XLSX.utils.encode_cell({ r: genelToplamRowIndex, c });
        if (!ws[ref]) continue;
        ws[ref].s = {
          ...(ws[ref].s || {}),
          font: { bold: true, color: { rgb: '000000' } },
          fill: { fgColor: { rgb: 'A6A6A6' } },
          alignment: { horizontal: c <= 1 ? 'right' : 'center', vertical: 'center' },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mizan');

    // Dosya adı: firma_vergikimlikno_yıl_dönem (cumulative) veya firma_vergikimlikno_yıl_ay (single)
    const companyPrefix = (
      selectedCompany?.name ||
      selectedCompany?.title ||
      selectedCompany?.companyName ||
      selectedCompany?.unvan ||
      companyInfo?.title ||
      companyInfo?.name ||
      'firma'
    )
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '');
    const vkn = (selectedIdentifier || 'export').replace(/[^a-zA-Z0-9]/g, '');
    let filename = 'mizan';
    if (viewMode === 'period' && selectedYear && selectedPeriod) {
      filename = `${companyPrefix}_${vkn}_${selectedYear}_${selectedPeriod}`;
    } else if (viewMode === 'single' && (selectedDocument || mizanData?.periodCoveredStart)) {
      const start = selectedDocument?.periodCoveredStart || mizanData?.periodCoveredStart;
      if (start) {
        const d = new Date(start);
        const y = d.getFullYear();
        const ay = String(d.getMonth() + 1).padStart(2, '0');
        filename = `${companyPrefix}_${vkn}_${y}_${ay}`;
      }
    } else {
      filename = `${companyPrefix}_mizan`;
    }
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const handleDownloadBalanceSheet = () => {
    const data = bilancoExportRef.current;
    if (!data || !data.aktifSonuc || !data.pasifSonuc) return;

    const toExcelNumber = (value) => {
      if (value === null || value === undefined) return 0;
      const n = parseFloat(value);
      return isNaN(n) ? 0 : Math.round(n * 100) / 100;
    };

    // Tek tabloda alt alta: başlık + Aktif + Pasif + Gelir Tablosu
    const header = ['Code', 'Title', 'Amount'];
    const rows = [header];

    // AKTİF bölümü
    rows.push(['', 'AKTİF', '']);
    data.aktifSonuc.forEach((item) => {
      if (item.type === 'kalem') {
        rows.push([item.accountCode || '', item.accountName || '', toExcelNumber(item.bilancoDegeri)]);
      } else if (item.type === 'toplam') {
        rows.push([item.kod || '', item.baslik || '', toExcelNumber(item.deger)]);
      }
    });
    if (data.aktifToplamDeger != null) {
      rows.push(['', 'AKTİF TOPLAMI', toExcelNumber(data.aktifToplamDeger)]);
    }
    rows.push([]);

    // PASİF bölümü
    rows.push(['', 'PASİF', '']);
    data.pasifSonuc.forEach((item) => {
      if (item.type === 'kalem') {
        rows.push([item.accountCode || '', item.accountName || '', toExcelNumber(item.bilancoDegeri)]);
      } else if (item.type === 'toplam') {
        rows.push([item.kod || '', item.baslik || '', toExcelNumber(item.deger)]);
      }
    });
    rows.push(['', 'PASIF TOPLAMI', toExcelNumber(data.pasifToplamDeger)]);
    rows.push([]);

    // GELİR TABLOSU bölümü (THP hiyerarşisi varsa onu kullan)
    rows.push(['', 'GELİR TABLOSU', '']);
    const gelirRows = data.gelirSonuc && data.gelirSonuc.length > 0
      ? data.gelirSonuc
      : (data.kalemler || []).filter((k) => k.ilkHane === '6');
    gelirRows.forEach((item) => {
      rows.push([item.accountCode || '', item.accountName || '', toExcelNumber(item.bilancoDegeri)]);
    });
    if (data.selectedPeriod !== '4') {
      if (data.donemNetKariDegeri >= 0) {
        rows.push(['', 'Brüt Kar', toExcelNumber(data.hesaplananDonemNetKari)]);
        rows.push(['', 'Ödenecek vergiler', toExcelNumber(data.donemNetKariDegeri2)]);
      }
      rows.push(['', data.donemNetKariDegeri >= 0 ? '590 - Dönem net karı' : '591 - Dönem net zararı', toExcelNumber(data.donemNetKariDegeri)]);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    setExcelNumericColumn(ws, 2);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');

    const companyPrefix = (
      selectedCompany?.name ||
      selectedCompany?.title ||
      selectedCompany?.companyName ||
      selectedCompany?.unvan ||
      companyInfo?.title ||
      companyInfo?.name ||
      'firma'
    )
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '');
    const vkn = (selectedIdentifier || 'balance').replace(/[^a-zA-Z0-9]/g, '');
    const filename = selectedYear && selectedPeriod
      ? `${companyPrefix}_${vkn}_${selectedYear}_${selectedPeriod}_balance_sheet`
      : `${companyPrefix}_${vkn}_balance_sheet`;
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  const setExcelNumericColumn = (ws, colIndex) => {
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let r = 1; r <= range.e.r; r++) {
      const ref = XLSX.utils.encode_cell({ r, c: colIndex });
      if (ws[ref] && typeof ws[ref].v === 'number') {
        ws[ref].t = 'n';
        ws[ref].z = '#,##0.00';
      }
    }
    for (let c = 0; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r: 0, c });
      if (ws[ref]) {
        ws[ref].t = 's';
        ws[ref].z = '@';
        if (typeof ws[ref].v !== 'string') ws[ref].v = String(ws[ref].v ?? '');
      }
    }
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return '';
    const start = formatDate(doc.periodCoveredStart);
    const end = formatDate(doc.periodCoveredEnd);
    return `${start} - ${end} (${doc.uniqueId || doc.id || ''})`;
  };

  
  const getDocumentKey = (doc) => {
    if (!doc) return '';
    // Use id as primary key, fallback to uniqueId + period if id not available
    return doc.id ? `doc-${doc.id}` : `doc-${doc.uniqueId}-${doc.periodCoveredStart}-${doc.periodCoveredEnd}`;
  };

  // Mizan verilerini account code'a göre grupla (hem single hem period modunda)
  const groupedMizanData = useMemo(() => {
    if (!mizanData || !mizanData.rows) return null;

    const groups = new Map();

    mizanData.rows.forEach((row) => {
      const accountCode = row.hesapKodu || '';
      if (!groups.has(accountCode)) {
        groups.set(accountCode, {
          accountCode,
          mainDescription: row.mainDescription || '',
          baseRow: null,
          subRows: [],
        });
      }

      const group = groups.get(accountCode);
      if (!group.mainDescription && row.mainDescription) {
        group.mainDescription = row.mainDescription;
      }

      // Sadece gerçek alt hesap varsa iç içe tabloya koy (Sub Code !== Account Code)
      const hesapKodu = (row.hesapKodu || '').trim();
      const subId = (row.subId || '').trim();
      if (subId && subId !== hesapKodu) {
        group.subRows.push(row);
      } else {
        group.baseRow = row;
      }
    });

    return Array.from(groups.values())
      .map((group) => {
        const totalsFromSubRows = group.subRows.reduce(
          (acc, row) => {
            acc.borc += row.borc || 0;
            acc.alacak += row.alacak || 0;
            acc.borcBakiyesi += row.borcBakiyesi || 0;
            acc.alacakBakiyesi += row.alacakBakiyesi || 0;
            return acc;
          },
          { borc: 0, alacak: 0, borcBakiyesi: 0, alacakBakiyesi: 0 }
        );

        const hasMultipleDetails = group.subRows.length >= 1; // 1 veya daha fazla alt satır varsa iç içe tablo göster
        const hasAnyDetail = group.subRows.length > 0;

        const totalBorc = hasAnyDetail ? totalsFromSubRows.borc : group.baseRow?.borc || 0;
        const totalAlacak = hasAnyDetail ? totalsFromSubRows.alacak : group.baseRow?.alacak || 0;
        // Debit Balance / Credit Balance: debit-credit>0 ise debit balance'a (debit-credit), credit balance 0; debit-credit<0 ise credit balance'a (credit-debit), debit balance 0
        const fark = totalBorc - totalAlacak;
        const totalBorcBakiyesi = fark > 0 ? fark : 0;
        const totalAlacakBakiyesi = fark < 0 ? -fark : 0;

        return {
          ...group,
          totalBorc,
          totalAlacak,
          totalBorcBakiyesi,
          totalAlacakBakiyesi,
          hasMultipleDetails,
        };
      })
      .sort((a, b) => {
        return (a.accountCode || '').localeCompare(b.accountCode || '');
      });
  }, [mizanData, viewMode]);

  const handleToggleRow = (accountCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(accountCode)) {
      newExpanded.delete(accountCode);
    } else {
      newExpanded.add(accountCode);
    }
    setExpandedRows(newExpanded);
  };

  // PDF upload handler
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Lütfen bir PDF dosyası seçin');
      return;
    }

    // Period mode için identifier ve period kontrolü
    if (viewMode === 'period') {
      if (!selectedIdentifier) {
        setError('Lütfen önce bir identifier seçin');
        return;
      }
      if (!selectedPeriod) {
        setError('Lütfen önce bir period seçin');
        return;
      }
    }

    try {
      setUploadingPdf(true);
      setError(null);

      const formData = new FormData();
      formData.append('pdfData', file);
      
      // Period mode için identifier ve period bilgilerini ekle
      if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
        const period = periods.find(p => p.value === selectedPeriod);
        if (period) {
          formData.append('identifier', selectedIdentifier);
          formData.append('periodStart', period.start);
          formData.append('periodEnd', period.end);
        }
      }

      const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/readVergiPDF`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPdfData(data);
        
        // Bilanço verisi kontrolü
        const hasBilanco = data.bilanco && data.bilanco.length > 0;
        const hasGelirTablosu = data.gelir_tablosu && data.gelir_tablosu.length > 0;
        
        if (!hasBilanco && hasGelirTablosu) {
          console.warn('[PDF Upload] ⚠️ PDF\'te bilanço verisi bulunamadı, sadece gelir tablosu var. Bilanço kalemleri (ör. 329) eşleşmeyecek.');
          setError('Uyarı: PDF\'te bilanço verisi bulunamadı. Sadece gelir tablosu okundu. Bilanço kalemleri için Declaration Balance değerleri görünmeyecek.');
        } else if (!hasBilanco && !hasGelirTablosu) {
          setError('PDF\'te ne bilanço ne de gelir tablosu bulunamadı. PDF formatını kontrol edin.');
        }
        
        // Önce yüklenen dosyayı göster (kullanıcı hemen görebilsin)
        const fileUrl = URL.createObjectURL(file);
        setPdfFile(fileUrl);
        setPdfFileName(file.name);
        
        // PDF başarıyla kaydedildiyse, kaydedilmiş PDF'i yükle (arka planda)
        if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
          // Kısa bir gecikme ekle (PDF'in kaydedilmesi için)
          setTimeout(async () => {
            await loadSavedPdf({ preserveFileName: true });
          }, 1000);
        }
        
        // PDF yüklendikten sonra PDF tab'ına geçme - kullanıcı manuel olarak tıklayabilir
        // setActiveTab(1);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'PDF okuma hatası');
      }
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setError('PDF yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setUploadingPdf(false);
    }
  };

  // Kaydedilmiş PDF'i yükleme (hem dosya hem de veri)
  const loadSavedPdf = async (options = {}) => {
    if (viewMode !== 'period' || !selectedIdentifier || !selectedPeriod) {
      return;
    }

    try {
      const period = periods.find(p => p.value === selectedPeriod);
      if (!period) return;

      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/getPdf?identifier=${encodeURIComponent(selectedIdentifier)}&periodStart=${encodeURIComponent(period.start)}&periodEnd=${encodeURIComponent(period.end)}`
      );

      if (response.ok) {
        const blob = await response.blob();
        const fileUrl = URL.createObjectURL(blob);
        // Eski URL'i temizle
        if (pdfFile) {
          URL.revokeObjectURL(pdfFile);
        }
        setPdfFile(fileUrl);
        if (!options.preserveFileName) {
          setPdfFileName(
            extractPdfFileName(response, selectedIdentifier, period.start, period.end)
          );
        }
        const formData = new FormData();
        formData.append('pdfData', blob, 'saved.pdf');
        formData.append('identifier', selectedIdentifier);
        formData.append('periodStart', period.start);
        formData.append('periodEnd', period.end);
        
        const pdfDataResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/readVergiPDF`, {
          method: 'POST',
          body: formData,
        });
        
        if (pdfDataResponse.ok) {
          const pdfDataResult = await pdfDataResponse.json();
          setPdfData(pdfDataResult);
        } else {
          const errorText = await pdfDataResponse.text();
          console.warn('⚠️ Failed to load saved PDF data:', errorText);
        }
      } 
    } catch (err) {
      console.error('❌ Error loading saved PDF:', err);
      // Hata durumunda mevcut PDF'i koru
    }
  };

  // PDF silme fonksiyonu
  const handlePdfDownload = () => {
    if (!pdfFile) return;
    const link = document.createElement('a');
    link.href = pdfFile;
    link.download = pdfFileName || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeletePdf = async () => {
    if (viewMode !== 'period' || !selectedIdentifier || !selectedPeriod) {
      return;
    }

    try {
      setDeletingPdf(true);
      setError(null);

      const period = periods.find(p => p.value === selectedPeriod);
      if (!period) {
        setError('Period bulunamadı');
        return;
      }

      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/deletePdf?identifier=${encodeURIComponent(selectedIdentifier)}&periodStart=${encodeURIComponent(period.start)}&periodEnd=${encodeURIComponent(period.end)}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        const result = await response.json();
        // PDF state'lerini temizle
        setPdfFile(null);
        setPdfFileName(null);
        setPdfData(null);
        setDeleteDialogOpen(false);
        setPdfPreviewOpen(false);
        
        // Başarı mesajı göster
        setError(null);
        // Başarı mesajını error state'inde göstermek yerine, geçici bir mesaj göster
        setTimeout(() => {
          // Mesaj otomatik kaybolacak
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'PDF silinirken hata oluştu');
      }
    } catch (err) {
      console.error('Error deleting PDF:', err);
      setError('PDF silinirken bir hata oluştu: ' + err.message);
    } finally {
      setDeletingPdf(false);
    }
  };

  // PDF saklama bilgisini al
  const getPdfStorageInfo = () => {
    if (viewMode === 'period' && selectedIdentifier && selectedPeriod) {
      const period = periods.find(p => p.value === selectedPeriod);
      if (period) {
        // Backend'deki dosya adı formatına göre bilgi oluştur
        const fileName = `${selectedIdentifier.replaceAll(/[^a-zA-Z0-9]/g, '_')}_${period.start.replaceAll(/[^a-zA-Z0-9]/g, '_')}_${period.end.replaceAll(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        return {
          location: 'dpg-app/Pdf',
          fileName: fileName,
          fullPath: `dpg-app/Pdf/${fileName}`
        };
      }
    }
    return null;
  };

  // PDF'ten okunan değeri account code'a göre bul
  const getPdfValueForAccount = (accountCode) => {
    if (!pdfData || !accountCode) {
      return null;
    }

    // İlk çağrıda PDF verisinin tamamını logla (sadece bir kez)
    if (!getPdfValueForAccount._logged) {      
      
      // Bilanço kodlarını listele
      if (pdfData.bilanco && pdfData.bilanco.length > 0) {
        const cariDonem = pdfData.bilanco.find(d => d.donem === 'cari');
        if (cariDonem && cariDonem.kalemler) {
          const bilancoKodlar = cariDonem.kalemler
            .map(k => ({ kod: k.kod, baslik: k.baslik }))
            .filter(k => k.kod)
            .sort((a, b) => (a.kod || '').localeCompare(b.kod || ''));
        }
      }
      
      // Gelir tablosu kodlarını listele
      if (pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0) {
        const cariDonem = pdfData.gelir_tablosu.find(d => d.donem === 'cari');
        if (cariDonem && cariDonem.kalemler) {
          const gelirKodlar = cariDonem.kalemler
            .map(k => ({ kod: k.kod, baslik: k.baslik }))
            .filter(k => k.kod)
            .sort((a, b) => (a.kod || '').localeCompare(b.kod || ''));
        }
      }
      
      getPdfValueForAccount._logged = true;
    }

    // Account code'u normalize et (trim, lowercase, "kod" önekini kaldır)
    const normalizeCode = (code) => {
      if (!code) return '';
      return String(code).trim().toLowerCase().replace(/^kod/, '');
    };

    const normalizedAccountCode = normalizeCode(accountCode);
   
    // Bilanço verilerini kontrol et
    if (pdfData.bilanco && pdfData.bilanco.length > 0) {
   
      // Cari dönem verilerini kullan
      const cariDonem = pdfData.bilanco.find(d => d.donem === 'cari');
      if (cariDonem && cariDonem.kalemler) {       
        // 329 kodunu özellikle ara
        const kod329 = cariDonem.kalemler.find(k => {
          const normalized = normalizeCode(k.kod);
          return normalized === '329' || normalized === 'kod329';
        });
        if (kod329) {
          console.log();
        } else {
          const allBilancoKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);
          console.log();
        }

        const kalem = cariDonem.kalemler.find(k => {
          if (!k.kod) return false;
          const normalizedPdfKod = normalizeCode(k.kod);
          
          // Tam eşleşme kontrolü
          if (normalizedPdfKod === normalizedAccountCode) {           
            return true;
          }
          
          // Sayısal kodlar için leading zero'ları yok say
          const pdfNum = normalizedPdfKod.replace(/^0+/, '') || '0';
          const accountNum = normalizedAccountCode.replace(/^0+/, '') || '0';
          if (pdfNum === accountNum) {
            return true;
          }
          
          return false;
        });
        
        if (kalem) {
          const deger = kalem.harcama || kalem.tutar || 0;
          return deger;
        } else {
          const allKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);        
        }
      }
    }

    // Gelir tablosu verilerini kontrol et
    if (pdfData.gelir_tablosu && pdfData.gelir_tablosu.length > 0) {
      const cariDonem = pdfData.gelir_tablosu.find(d => d.donem === 'cari');
      if (cariDonem && cariDonem.kalemler) {
        

        const kalem = cariDonem.kalemler.find(k => {
          if (!k.kod) return false;
          const normalizedPdfKod = normalizeCode(k.kod);
          
          // Tam eşleşme kontrolü
          if (normalizedPdfKod === normalizedAccountCode) {            
            return true;
          }
          
          // Sayısal kodlar için leading zero'ları yok say
          const pdfNum = normalizedPdfKod.replace(/^0+/, '') || '0';
          const accountNum = normalizedAccountCode.replace(/^0+/, '') || '0';
          if (pdfNum === accountNum) {           
            return true;
          }
          
          return false;
        });
        
        if (kalem) {
          const deger = kalem.tutar || kalem.harcama || 0;
          return deger;
        } else {
          const allKodlar = cariDonem.kalemler.map(k => ({
            kod: k.kod,
            normalized: normalizeCode(k.kod),
            baslik: k.baslik
          })).filter(k => k.kod);
         
        }
      }
    }

    return null;
  };

  // Toplamları hesapla: iç içe tablodaki (subRows) satırların toplamı kullanılır; alt satır yoksa grup toplamı
  const totals = useMemo(() => {
    if (!groupedMizanData || groupedMizanData.length === 0) {
      return {
        totalBorc: 0,
        totalAlacak: 0,
        totalBorcBakiyesi: 0,
        totalAlacakBakiyesi: 0,
        totalDeclarationBalance: 0,
      };
    }

    const result = groupedMizanData.reduce(
      (acc, group) => {
        const hasSubRows = group.subRows && group.subRows.length > 0;
        if (hasSubRows) {
          group.subRows.forEach((row) => {
            acc.totalBorc += parseFloat(row.borc) || 0;
            acc.totalAlacak += parseFloat(row.alacak) || 0;
            acc.totalBorcBakiyesi += parseFloat(row.borcBakiyesi) || 0;
            acc.totalAlacakBakiyesi += parseFloat(row.alacakBakiyesi) || 0;
          });
        } else {
          acc.totalBorc += parseFloat(group.totalBorc) || 0;
          acc.totalAlacak += parseFloat(group.totalAlacak) || 0;
          acc.totalBorcBakiyesi += parseFloat(group.totalBorcBakiyesi) || 0;
          acc.totalAlacakBakiyesi += parseFloat(group.totalAlacakBakiyesi) || 0;
        }
        return acc;
      },
      {
        totalBorc: 0,
        totalAlacak: 0,
        totalBorcBakiyesi: 0,
        totalAlacakBakiyesi: 0,
        totalDeclarationBalance: 0,
      }
    );

    // Declaration Balance toplamı (period mode için)
    if (viewMode === 'period' && pdfData && groupedMizanData) {
      groupedMizanData.forEach((group) => {
        const pdfValue = getPdfValueForAccount(group.accountCode);
        if (pdfValue !== null && pdfValue !== undefined) {
          result.totalDeclarationBalance += parseFloat(pdfValue) || 0;
        }
      });
    }

    return result;
  }, [groupedMizanData, viewMode, pdfData]);

  // Gelir tablosu hesabı mı (6xx, 7xx). Beyannamede kapatılmış hesaplarda bakiye 0, tutar hareket toplamı olarak görünür.
  const isGelirTablosuAccount = (accountCode) => {
    if (!accountCode) return false;
    const normalized = String(accountCode).trim().toLowerCase().replace(/^0+/, '');
    const first = normalized.charAt(0);
    return first === '6' || first === '7';
  };

  const round2 = (v) => Math.round(Number(v) * 100) / 100;

  // PDF değerinin debit/credit balance veya (gelir tablosu kapatılmışsa) hareket toplamı ile eşleşip eşleşmediğini kontrol et
  // options: { accountCode, borc, alacak } - gelir tablosu (6/7) hesapları kapatılmışsa (bakiye 0) hareket toplamı ile karşılaştırılır
  const isPdfValueMatching = (pdfValue, debitBalance, creditBalance, options = {}) => {
    if (pdfValue === null || pdfValue === undefined) return false;
    const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
    const debitVal = Math.abs(parseFloat(debitBalance) || 0);
    const creditVal = Math.abs(parseFloat(creditBalance) || 0);
    const pdfR = round2(pdfVal);
    const debitR = round2(debitVal);
    const creditR = round2(creditVal);
    const tolerance = 0.01;
    const match = (a, b) => Math.abs(a - b) < tolerance;

    // Gelir tablosu (6/7) hesapları kapatılmışsa (borç/alacak bakiyesi 0) → Declaration ile hareket toplamını karşılaştır
    const { accountCode, borc, alacak } = options;
    if (accountCode && isGelirTablosuAccount(accountCode) && debitR === 0 && creditR === 0) {
      const hareketToplami = Math.max(parseFloat(borc) || 0, parseFloat(alacak) || 0);
      const hareketR = round2(hareketToplami);
      return match(pdfR, hareketR);
    }

    // Bakiye ile karşılaştırma (mevcut mantık)
    if (debitR > 0 && creditR === 0) return match(pdfR, debitR);
    if (creditR > 0 && debitR === 0) return match(pdfR, creditR);
    if (debitR > 0 && creditR > 0) return match(pdfR, debitR) || match(pdfR, creditR);
    return pdfR === 0;
  };

  // ViewMode değiştiğinde expanded rows'u temizle
  useEffect(() => {
    setExpandedRows(new Set());
    // Single mode'a geçildiğinde tab'ı Table'a resetle
    if (viewMode === 'single') {
      setActiveTab(0);
    }
  }, [viewMode]);

  // Cleanup PDF URL on unmount
  useEffect(() => {
    return () => {
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);

  const yearOptions = useMemo(
    () => availableYears.map((y) => ({ value: y, label: String(y) })),
    [availableYears]
  );
  const selectedYearOption = useMemo(
    () => (selectedYear ? { value: selectedYear, label: String(selectedYear) } : null),
    [selectedYear]
  );

  const documentOptions = useMemo(
    () =>
      filteredDocuments.map((doc) => ({
        value: doc.id ?? getDocumentKey(doc),
        label: getDocumentLabel(doc),
        doc,
      })),
    [filteredDocuments]
  );

  const selectedDocOption = useMemo(() => {
    if (!selectedDocument) return null;
    return (
      documentOptions.find((o) => {
        const d = o.doc;
        if (selectedDocument.id && d.id) return d.id === selectedDocument.id;
        return (
          d.uniqueId === selectedDocument.uniqueId &&
          d.periodCoveredStart === selectedDocument.periodCoveredStart &&
          d.periodCoveredEnd === selectedDocument.periodCoveredEnd
        );
      }) ?? null
    );
  }, [selectedDocument, documentOptions]);

  const periodOptions = useMemo(
    () => periods.map((p) => ({ value: p.value, label: p.label, period: p })),
    [periods]
  );

  const selectedPeriodOption = useMemo(() => {
    if (!selectedPeriod) return null;
    return periodOptions.find((o) => o.value === selectedPeriod) ?? null;
  }, [selectedPeriod, periodOptions]);

  const onYearChange = (option) => {
    setSelectedYear(option ? option.value : null);
    setSelectedDocument(null);
    setSelectedPeriod(null);
    setMizanData(null);
    setPdfData(null);
    if (pdfFile) {
      URL.revokeObjectURL(pdfFile);
      setPdfFile(null);
      setPdfFileName(null);
    }
    setError(null);
  };

  const onDocumentSelectChange = (option) => {
    if (option) handleDocumentChange(null, option.doc, 'selectOption');
    else handleDocumentChange(null, null, 'clear');
  };

  const onPeriodSelectChange = (option) => {
    if (option) handlePeriodChange(null, option.period, 'selectOption');
    else handlePeriodChange(null, null, 'clear');
  };

  return (
    <div className={embedded ? '' : 'space-y-6 animate-in fade-in duration-500'}>
      {!embedded && (
        <div className="flex justify-between items-center gap-4">
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white hidden md:block">
            {t('edefter.trialBalanceTitle')}
          </h2>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {trialBalanceSourceTab === 1 && (
              <button
                type="button"
                onClick={openUploadModal}
                className="flex items-center gap-2 px-4 py-2 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 shrink-0"
              >
                <Upload size={16} />
                {t('edefter.uploadNewTrialBalance')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className={embedded ? 'bg-white dark:bg-[#1C1C1E] overflow-hidden' : `${cardClass} overflow-hidden`}>
        {!embedded && (
          <div className="px-6 md:px-8 flex flex-wrap border-b border-gray-100 dark:border-white/5">
            <button
              type="button"
              onClick={() => setTrialBalanceSourceTab(0)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                trialBalanceSourceTab === 0
                  ? 'text-[#24299d] dark:text-[#818cf8] shadow-[inset_0_-2px_0_0_#24299d] dark:shadow-[inset_0_-2px_0_0_#818cf8]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <BookOpen size={16} />
              {t('edefter.trialBalanceFromJournal')}
            </button>
            <button
              type="button"
              onClick={() => setTrialBalanceSourceTab(1)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                trialBalanceSourceTab === 1
                  ? 'text-[#24299d] dark:text-[#818cf8] shadow-[inset_0_-2px_0_0_#24299d] dark:shadow-[inset_0_-2px_0_0_#818cf8]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <FileSpreadsheet size={16} />
              {t('edefter.trialBalanceFromFile')}
            </button>
          </div>
        )}

        <div className={embedded ? 'p-4 md:p-6' : 'p-6 md:p-8 space-y-8'}>
            {trialBalanceSourceTab === 0 ? (
              <>
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl px-4 py-3">
                    <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium flex-1">{error}</p>
                    <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                      <X size={14} />
                    </button>
                  </div>
                )}

                {viewMode === 'period' && missingMizanDocuments.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <p className="text-xs text-amber-800 dark:text-amber-300 font-medium flex-1">
                        {t('edefter.missingMizanTitle', { count: missingMizanDocuments.length })}
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={handleCalculateAllMissingMizans}
                          disabled={loading || calculatingMizanForDoc.size > 0}
                          className="px-3 py-1.5 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors"
                        >
                          {loading || calculatingMizanForDoc.size > 0 ? t('edefter.calculating') : t('edefter.calculateAll')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowMissingMizanList(!showMissingMizanList)}
                          className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                          aria-label={showMissingMizanList ? 'Hide details' : 'Show details'}
                        >
                          {showMissingMizanList ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      </div>
                    </div>
                    {showMissingMizanList && (
                      <div className="mt-3 space-y-2">
                        {missingMizanDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white dark:bg-white/5 rounded-xl border border-amber-100 dark:border-amber-800/30"
                          >
                            <div>
                              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                                {formatDate(doc.periodCoveredStart)} - {formatDate(doc.periodCoveredEnd)}
                              </p>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                {doc.entryCount || 0} journal entries
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCalculateMizanForDocument(doc.id)}
                              disabled={calculatingMizanForDoc.has(doc.id)}
                              className="px-3 py-1.5 border border-[#24299d] text-[#24299d] dark:text-[#818cf8] dark:border-[#818cf8] rounded-lg text-xs font-semibold hover:bg-[#24299d]/5 disabled:opacity-50 transition-colors"
                            >
                              {calculatingMizanForDoc.has(doc.id) ? t('edefter.calculating') : t('edefter.calculate')}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

            <div>
              <SectionHeader icon={SlidersHorizontal} label={t('edefter.viewMode')} />

              {!selectedIdentifier && (
                <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl px-4 py-3 mb-4">
                  <AlertTriangle size={15} className="text-blue-500 flex-shrink-0" />
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">{t('edefter.noCompanySelected')}</p>
                </div>
              )}

              <div className="mb-5">
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full sm:max-w-md"
                  role="radiogroup"
                  aria-label={t('edefter.viewMode')}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={viewMode === 'single'}
                    disabled={!selectedYear}
                    onClick={() => handleViewModeChange({ target: { value: 'single' } })}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl border transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                      viewMode === 'single'
                        ? 'border-[#24299d] bg-[#24299d]/[0.06] dark:bg-[#24299d]/20'
                        : 'border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/20'
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg shrink-0 transition-colors ${
                        viewMode === 'single'
                          ? 'bg-[#24299d] text-white'
                          : 'bg-white dark:bg-white/10 text-gray-400'
                      }`}
                    >
                      <CalendarDays size={15} />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-bold leading-tight ${
                          viewMode === 'single'
                            ? 'text-[#24299d] dark:text-[#818cf8]'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {t('edefter.singleMonth')}
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    role="radio"
                    aria-checked={viewMode === 'period'}
                    disabled={!selectedYear}
                    onClick={() => handleViewModeChange({ target: { value: 'period' } })}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl border transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                      viewMode === 'period'
                        ? 'border-[#24299d] bg-[#24299d]/[0.06] dark:bg-[#24299d]/20'
                        : 'border-gray-200 dark:border-white/10 bg-gray-50/50 dark:bg-white/[0.03] hover:border-gray-300 dark:hover:border-white/20'
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg shrink-0 transition-colors ${
                        viewMode === 'period'
                          ? 'bg-[#24299d] text-white'
                          : 'bg-white dark:bg-white/10 text-gray-400'
                      }`}
                    >
                      <CalendarRange size={15} />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-bold leading-tight ${
                          viewMode === 'period'
                            ? 'text-[#24299d] dark:text-[#818cf8]'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {t('edefter.cumulativePeriod')}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {!selectedYear && selectedIdentifier && availableYears.length === 0 && (
                <p className="text-xs text-gray-400 -mt-2 mb-4">{t('edefter.noDataFound')}</p>
              )}
              {!selectedYear && selectedIdentifier && availableYears.length > 0 && (
                <p className="text-xs text-gray-400 -mt-2 mb-4">{t('edefter.selectYear')}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                {selectedIdentifier && availableYears.length > 0 && (
                  <div className="md:col-span-4 space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Calendar size={13} className="text-[#24299d] dark:text-[#818cf8]" />
                      {t('edefter.selectYear')}
                    </label>
                    <Select
                      options={yearOptions}
                      value={selectedYearOption}
                      onChange={onYearChange}
                      placeholder={t('edefter.selectYear')}
                      isClearable
                      isDisabled={!selectedIdentifier || loadingDocuments}
                      styles={getSelectStyles(darkMode)}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                )}

                {viewMode === 'single' && (
                  <div className="md:col-span-4 space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Calendar size={13} className="text-[#24299d] dark:text-[#818cf8]" />
                      {t('edefter.journalPeriod')}
                    </label>
                    <Select
                      options={documentOptions}
                      value={selectedDocOption}
                      onChange={onDocumentSelectChange}
                      placeholder={t('edefter.selectPeriod')}
                      isLoading={loadingDocuments}
                      isClearable
                      isDisabled={!selectedIdentifier || loadingDocuments || !selectedYear}
                      styles={getSelectStyles(darkMode)}
                      noOptionsMessage={() => t('edefter.noDataFound')}
                      loadingMessage={() => t('edefter.loadingDocuments')}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                )}

                {viewMode === 'period' && (
                  <div className="md:col-span-4 space-y-1.5">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <Calendar size={13} className="text-[#24299d] dark:text-[#818cf8]" />
                      {t('edefter.selectCumulativePeriod')}
                    </label>
                    <Select
                      options={periodOptions}
                      value={selectedPeriodOption}
                      onChange={onPeriodSelectChange}
                      placeholder={t('edefter.selectCumulativePeriod')}
                      isClearable
                      isDisabled={!selectedIdentifier || !selectedYear}
                      styles={getSelectStyles(darkMode)}
                      noOptionsMessage={() => t('edefter.noDataFound')}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Loading State */}
            {loading && ((selectedDocument && viewMode === 'single') || (selectedPeriod && viewMode === 'period')) && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 size={40} className="text-[#24299d] animate-spin" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('edefter.loadingTrialBalance')}</p>
                {viewMode === 'single' && (
                  <p className="text-xs text-gray-400">{t('edefter.loadingTrialBalanceHint')}</p>
                )}
              </div>
            )}

            {/* Empty State - Period selected but no data */}
            {!loading && selectedPeriod && viewMode === 'period' && mizanData && (!mizanData.rows || mizanData.rows.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Description sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  No trial balance data found for this period
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Trial balance data may not have been created for documents in the selected period. 
                  Automatic calculation was attempted but data could not be created. Please try manually.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError(null);
                      const period = periods.find(p => p.value === selectedPeriod);
                      if (!period) {
                        setError('Period not found');
                        return;
                      }
                      
                      // Period içindeki belgeleri bul ve hesapla
                      const periodStartDate = new Date(period.start);
                      const periodEndDate = new Date(period.end);
                      const periodDocuments = documents.filter(doc => {
                        if (!doc.periodCoveredStart || !doc.periodCoveredEnd) return false;
                        const docStart = new Date(doc.periodCoveredStart);
                        const docEnd = new Date(doc.periodCoveredEnd);
                        return docStart <= periodEndDate && docEnd >= periodStartDate;
                      });
                      
                      if (periodDocuments.length === 0) {
                        setError('No documents found for the selected period.');
                        return;
                      }
                      
                      let calculatedCount = 0;
                      for (const doc of periodDocuments) {
                        if (doc.id) {
                          try {
                            const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${doc.id}`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                              },
                            });
                            
                            if (calculateResponse.ok) {
                              const calculateResult = await calculateResponse.json();
                              if (calculateResult.success) {
                                calculatedCount++;
                              }
                            }
                          } catch (calcErr) {
                            console.error(`Error calculating mizan for document ${doc.id}:`, calcErr);
                          }
                        }
                      }
                      
                      if (calculatedCount > 0) {
                        // Period mizan verisini tekrar çek
                        const retryResponse = await AuthService.authenticatedFetch(
                          `${EDEFTER_API_URL}/mizan-by-period?identifier=${selectedIdentifier}&periodStart=${period.start}&periodEnd=${period.end}`
                        );
                        if (retryResponse.ok) {
                          const retryData = await retryResponse.json();
                          setMizanData(retryData);
                          if (retryData.rows && retryData.rows.length > 0) {
                            setError(null);
                          } else {
                            setError(`Trial balance was calculated for ${calculatedCount} document(s) but no aggregated data was found.`);
                          }
                        }
                      } else {
                        setError('Failed to calculate trial balance for any documents in this period.');
                      }
                    } catch (err) {
                      console.error('Error calculating period mizan:', err);
                      setError(`Error calculating trial balance: ${err.message}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Calculate Trial Balance for Period
                </Button>
              </Box>
            )}

            {/* Empty State - Document selected but no data */}
            {!loading && selectedDocument && viewMode === 'single' && mizanData && (!mizanData.rows || mizanData.rows.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Description sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  No trial balance data found for this document
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Trial balance data may not have been created for the selected journal book. 
                  Automatic calculation was attempted but data could not be created. Please try manually.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      setError(null);
                      const calculateResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/calculate-mizan/${selectedDocument.id}`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                      });
                      
                      if (calculateResponse.ok) {
                        const calculateResult = await calculateResponse.json();
                        if (calculateResult.success) {
                          // Hesaplama başarılıysa, mizan verisini tekrar çek
                          const retryResponse = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/mizan-by-document?documentId=${selectedDocument.id}`);
                          if (retryResponse.ok) {
                            const retryData = await retryResponse.json();
                            setMizanData(retryData);
                            if (retryData.rows && retryData.rows.length > 0) {
                              setError(null);
                            } else {
                              setError('Trial balance was calculated but no rows were found for this document.');
                            }
                          }
                        } else {
                          setError(calculateResult.error || 'Failed to calculate trial balance');
                        }
                      } else {
                        const errorText = await calculateResponse.text();
                        setError(`Failed to calculate trial balance: ${errorText}`);
                      }
                    } catch (err) {
                      console.error('Error calculating mizan:', err);
                      setError(`Error calculating trial balance: ${err.message}`);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Calculate Trial Balance
                </Button>
              </Box>
            )}


            {/* Mizan Table and PDF Viewer with Tabs */}
            {mizanData && mizanData.rows && mizanData.rows.length > 0 && (
              <div>
                {viewMode === 'period' && (
                  <div className={`${cardClass} p-4 md:p-5 mb-4`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-11 h-11 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center shrink-0">
                          <FileText size={20} className="text-red-500" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                            {t('edefter.taxReturnDocument')}
                          </h4>
                          {pdfFile ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                              {pdfFileName || t('edefter.pdfDocumentLabel')}
                            </p>
                          ) : (
                            <>
                              <p className="text-xs text-gray-400 mt-0.5">{t('edefter.noPdfUploaded')}</p>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                                {t('edefter.noTaxReturnHint')}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0 sm:justify-end">
                        {pdfFile ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setPdfPreviewOpen(true)}
                              className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-500 hover:text-blue-600 transition-colors"
                                 >
                              <Eye size={14} />
                            </button>
                            <button
                              type="button"
                              title={t('edefter.downloadDocument')}
                              onClick={handlePdfDownload}
                              className="p-2 rounded-xl bg-gray-100 dark:bg-white/10 hover:bg-green-100 dark:hover:bg-green-900/30 text-gray-500 hover:text-green-600 transition-colors"
                            >
                              <Download size={16} />
                            </button>
                            <button
                              type="button"
                              title={t('edefter.deletePdf')}
                              onClick={() => setDeleteDialogOpen(true)}
                              disabled={deletingPdf}
                              className="p-2 rounded-xl bg-gray-100 dark:bg-white/10 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-500 hover:text-red-600 transition-colors disabled:opacity-50"
                            >
                              {deletingPdf ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            </button>
                          </>
                        ) : (
                          <>
                            <input
                              accept="application/pdf"
                              className="hidden"
                              id="pdf-upload-button"
                              type="file"
                              onChange={handlePdfUpload}
                              disabled={uploadingPdf}
                            />
                            <label htmlFor="pdf-upload-button">
                              <span
                                className={`inline-flex items-center gap-2 px-4 py-2 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 cursor-pointer ${
                                  uploadingPdf ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {uploadingPdf ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                {uploadingPdf ? t('edefter.uploading') : t('edefter.uploadPdf')}
                              </span>
                            </label>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <SectionHeader
                  icon={FileSpreadsheet}
                  label={t('edefter.trialBalanceTableTitle')}
                  subtitle={
                    mizanData.periodCoveredStart && mizanData.periodCoveredEnd ? (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={13} />
                        {formatDate(mizanData.periodCoveredStart)} - {formatDate(mizanData.periodCoveredEnd)}
                      </span>
                    ) : null
                  }
                  right={
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {(viewMode === 'single' || activeTab === 0) && (
                        <button
                          type="button"
                          onClick={handleDownloadExcel}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95 whitespace-nowrap"
                        >
                          <Download size={16} />
                          {t('edefter.downloadTrialBalance')}
                        </button>
                      )}
                      {viewMode === 'period' && activeTab === 1 && (groupedMizanData?.length > 0 || (selectedPeriod === '4' && period4BilancoFromYevmiye?.length > 0)) && (
                        <button
                          type="button"
                          onClick={handleDownloadBalanceSheet}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all active:scale-95 whitespace-nowrap"
                        >
                          <Download size={16} />
                          {t('edefter.downloadBalanceSheet')}
                        </button>
                      )}
                    </div>
                  }
                />
                {viewMode === 'period' && (
                  <div className="flex flex-wrap border-b border-gray-100 dark:border-white/5 mb-4">
                    <button
                      type="button"
                      onClick={() => setActiveTab(0)}
                      className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
                        activeTab === 0
                          ? 'text-[#24299d] dark:text-[#818cf8] shadow-[inset_0_-2px_0_0_#24299d] dark:shadow-[inset_0_-2px_0_0_#818cf8]'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <FileSpreadsheet size={16} />
                      {t('edefter.tabTable')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab(1)}
                      disabled={!(groupedMizanData?.length > 0 || (selectedPeriod === '4' && (period4BilancoFromYevmiye?.length > 0 || period4BilancoLoading)))}
                      className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${
                        activeTab === 1
                          ? 'text-[#24299d] dark:text-[#818cf8] shadow-[inset_0_-2px_0_0_#24299d] dark:shadow-[inset_0_-2px_0_0_#818cf8]'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                      }`}
                    >
                      <Scale size={16} />
                      {t('edefter.tabBalanceSheet')}
                    </button>
                  </div>
                )}

                {(viewMode === 'single' || activeTab === 0) && (
                <div className="rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden">
                    <TableContainer sx={getTableContainerSx(darkMode)}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={getTableHeadRowSx(darkMode)}>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Code
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Sub Code
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Name
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Account Detail Name
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Debit
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Credit
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Debit Balance
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Credit Balance
                          </TableCell>
                          {viewMode === 'period' && (
                            <TableCell sx={{ textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                              Declaration Balance
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedMizanData ? (
                          // Gruplanmış görünüm (hem single hem period mode)
                          groupedMizanData.map((group, groupIndex) => {
                            const isExpanded = expandedRows.has(group.accountCode);
                            const hasMultipleDetails = group.hasMultipleDetails;
                            const hasAnyDetail = group.subRows.length > 0;

                            const renderSimpleRow = (rowData) => (
                              <TableRow
                                key={`group-simple-${group.accountCode}-${groupIndex}`}
                                hover
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                  borderLeft: '4px solid transparent',
                                }}
                              >
                                <TableCell sx={{ width: 50 }} />
                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {rowData.hesapKodu || group.accountCode}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {rowData.subId || ''}
                                </TableCell>
                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                  {rowData.mainDescription || group.mainDescription}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                  {rowData.subDescription || ''}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.borc ?? group.totalBorc)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.alacak ?? group.totalAlacak)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.borcBakiyesi ?? group.totalBorcBakiyesi)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                  {formatNumber(rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi)}
                                </TableCell>
                                {viewMode === 'period' && (
                                  <TableCell sx={{ 
                                    textAlign: 'right', 
                                    fontWeight: 600, 
                                    fontSize: '0.8rem',
                                    color: (() => {
                                      const pdfValue = getPdfValueForAccount(rowData.hesapKodu || group.accountCode);
                                      const debitBalance = rowData.borcBakiyesi ?? group.totalBorcBakiyesi;
                                      const creditBalance = rowData.alacakBakiyesi ?? group.totalAlacakBakiyesi;
                                      const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                      const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                      
                                      if (pdfValue === null || pdfValue === undefined) {
                                        return 'text.primary';
                                      }
                                      
                                      const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                      
                                      // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                      if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                        return 'error.main';
                                      }
                                      
                                      // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                      if (pdfVal === 0) {
                                        return 'text.primary';
                                      }
                                      
                                      // PDF değeri var, eşleşme kontrolü yap (6/7 kapatılmışsa hareket toplamı ile)
                                      return isPdfValueMatching(
                                        pdfValue,
                                        debitBalance,
                                        creditBalance,
                                        { accountCode: rowData.hesapKodu || group.accountCode, borc: rowData.borc, alacak: rowData.alacak }
                                      ) ? 'success.main' : 'error.main';
                                    })()
                                  }}>
                                    {formatNumber(getPdfValueForAccount(rowData.hesapKodu || group.accountCode))}
                                  </TableCell>
                                )}
                              </TableRow>
                            );

                            if (!hasAnyDetail || (hasAnyDetail && !hasMultipleDetails)) {
                              const rowToRender = group.subRows[0] || group.baseRow || {
                                hesapKodu: group.accountCode,
                                subId: '',
                                mainDescription: group.mainDescription,
                                subDescription: '',
                                borc: group.totalBorc,
                                alacak: group.totalAlacak,
                                borcBakiyesi: group.totalBorcBakiyesi,
                                alacakBakiyesi: group.totalAlacakBakiyesi,
                              };
                              return renderSimpleRow(rowToRender);
                            }

                            return (
                              <React.Fragment key={`group-${group.accountCode}-${groupIndex}`}>
                                {/* Ana satır (toplam) */}
                                <TableRow 
                                  hover
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    },
                                    borderLeft: '4px solid transparent',
                                    backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                  }}
                                >
                                  <TableCell sx={{ width: 50 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleRow(group.accountCode)}
                                      sx={{ p: 0.5 }}
                                    >
                                      {isExpanded ? (
                                        <ExpandMore sx={{ fontSize: 20 }} />
                                      ) : (
                                        <ChevronRight size={20} className="text-gray-400" />
                                      )}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {group.accountCode}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {/* Top-level satırda sub code gösterme */}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem', fontWeight: 600 }}>
                                    {group.mainDescription}
                                  </TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                    Total
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalBorc)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalAlacak)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalBorcBakiyesi)}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                    {formatNumber(group.totalAlacakBakiyesi)}
                                  </TableCell>
                                  {viewMode === 'period' && (
                                    <TableCell sx={{ 
                                      textAlign: 'right', 
                                      fontWeight: 600, 
                                      fontSize: '0.8rem',
                                      color: (() => {
                                        const pdfValue = getPdfValueForAccount(group.accountCode);
                                        const debitBalance = group.totalBorcBakiyesi;
                                        const creditBalance = group.totalAlacakBakiyesi;
                                        const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                        const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                        
                                        if (pdfValue === null || pdfValue === undefined) {
                                          return 'text.primary';
                                        }
                                        
                                        const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                        
                                        // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                        if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                          return 'error.main';
                                        }
                                        
                                        // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                        if (pdfVal === 0) {
                                          return 'text.primary';
                                        }
                                        
                                        // PDF değeri var, eşleşme kontrolü yap (6/7 kapatılmışsa hareket toplamı ile)
                                        return isPdfValueMatching(
                                          pdfValue,
                                          debitBalance,
                                          creditBalance,
                                          { accountCode: group.accountCode, borc: group.totalBorc, alacak: group.totalAlacak }
                                        ) ? 'success.main' : 'error.main';
                                      })()
                                    }}>
                                      {formatNumber(getPdfValueForAccount(group.accountCode))}
                                    </TableCell>
                                  )}
                                </TableRow>
                                
                                {/* Detay satırları (Collapse içinde) */}
                                <TableRow>
                                  <TableCell colSpan={viewMode === 'period' ? 10 : 9} sx={{ py: 0, border: 'none' }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 0 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={getNestedTableHeadRowSx(darkMode)}>
                                              <TableCell sx={{ width: 50 }} />
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Code
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Sub Code
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Name
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Detail Name
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Debit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Credit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Debit Balance
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Credit Balance
                                              </TableCell>
                                              {viewMode === 'period' && (
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                  Declaration Balance
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {group.subRows.map((row, rowIndex) => (
                                              <TableRow 
                                                key={`detail-${group.accountCode}-${row.subId}-${rowIndex}`}
                                                sx={{
                                                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                  '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                  },
                                                }}
                                              >
                                                <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                <TableCell sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {row.hesapKodu || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                  {row.subId || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {row.mainDescription || ''}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                  {row.subDescription || ''}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.borc)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.alacak)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.borcBakiyesi)}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', fontSize: '0.8rem' }}>
                                                  {formatNumber(row.alacakBakiyesi)}
                                                </TableCell>
                                                {viewMode === 'period' && (
                                                  <TableCell sx={{ 
                                                    textAlign: 'right', 
                                                    fontWeight: 600, 
                                                    fontSize: '0.8rem',
                                                    color: (() => {
                                                      // Subcode'lar için renklendirme yok
                                                      if (row.subId) {
                                                        return 'text.secondary';
                                                      }
                                                      
                                                      const pdfValue = getPdfValueForAccount(row.hesapKodu);
                                                      const debitBalance = row.borcBakiyesi;
                                                      const creditBalance = row.alacakBakiyesi;
                                                      const debitVal = Math.abs(parseFloat(debitBalance) || 0);
                                                      const creditVal = Math.abs(parseFloat(creditBalance) || 0);
                                                      
                                                      if (pdfValue === null || pdfValue === undefined) {
                                                        return 'text.primary';
                                                      }
                                                      
                                                      const pdfVal = Math.abs(parseFloat(pdfValue) || 0);
                                                      
                                                      // Eğer debit veya credit balance dolu ama PDF değeri 0 ise kırmızı
                                                      if (pdfVal === 0 && (debitVal > 0 || creditVal > 0)) {
                                                        return 'error.main';
                                                      }
                                                      
                                                      // PDF değeri 0 ise ve balance'lar da 0 ise normal renk
                                                      if (pdfVal === 0) {
                                                        return 'text.primary';
                                                      }
                                                      
                                                      // PDF değeri var, eşleşme kontrolü yap (6/7 kapatılmışsa hareket toplamı ile)
                                                      return isPdfValueMatching(
                                                        pdfValue,
                                                        debitBalance,
                                                        creditBalance,
                                                        { accountCode: row.hesapKodu, borc: row.borc, alacak: row.alacak }
                                                      ) ? 'success.main' : 'error.main';
                                                    })()
                                                  }}>
                                                    {/* Subcode'lar için PDF'te ayrı değer yok, boş bırakılıyor */}
                                                    {row.subId ? '' : formatNumber(getPdfValueForAccount(row.hesapKodu))}
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          // Veri yoksa boş durum
                          <TableRow>
                            <TableCell colSpan={viewMode === 'period' ? 10 : 9} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {loading ? 'Yükleniyor...' : 'Veri bulunamadı'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {groupedMizanData && groupedMizanData.length > 0 && (
                          <TableRow sx={getTotalsRowSx(darkMode)}>
                            <TableCell colSpan={5}>
                              TOTAL
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right' }}>
                              {formatNumber(totals.totalBorc)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right' }}>
                              {formatNumber(totals.totalAlacak)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', color: darkMode ? '#93c5fd' : '#1976d2' }}>
                              {formatNumber(totals.totalBorcBakiyesi)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right', color: darkMode ? '#93c5fd' : '#1976d2' }}>
                              {formatNumber(totals.totalAlacakBakiyesi)}
                            </TableCell>
                            {viewMode === 'period' && (
                              <TableCell sx={{ textAlign: 'right' }}>
                                {formatNumber(totals.totalDeclarationBalance)}
                              </TableCell>
                            )}
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
                )}

                  {viewMode === 'period' && activeTab === 1 && (groupedMizanData?.length > 0 || (selectedPeriod === '4' && (period4BilancoFromYevmiye?.length > 0 || period4BilancoLoading || period4BilancoError))) && (() => {
                    // 4. dönem: yevmiye verisi yükleniyor veya hata
                    if (selectedPeriod === '4' && period4BilancoLoading) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                          <CircularProgress size={48} />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            Kapanış ayı yevmiye verisi yükleniyor...
                          </Typography>
                        </Box>
                      );
                    }
                    if (selectedPeriod === '4' && period4BilancoError && !(period4BilancoFromYevmiye?.length > 0)) {
                      return (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Alert severity="warning">{period4BilancoError}</Alert>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {period4BilancoError.includes('Kapanış ayı belgesi bulunamadı')
                              ? 'Seçilen yılda 4. dönemin son ayına ait aylık general journal yükleyin (Edefter Upload / General Journal).'
                              : '4. dönem bilançosu kapanış ayı yevmiyesinden hesaplanır; mizan kullanılmaz.'}
                          </Typography>
                        </Box>
                      );
                    }
                    // Veri kaynağı: 4. dönemde yevmiye, diğer dönemlerde mizan
                    let sourceData = (selectedPeriod === '4' && period4BilancoFromYevmiye?.length > 0)
                      ? period4BilancoFromYevmiye
                      : (groupedMizanData || []);
                    // 4. dönemde 690/692 yevmiyede olmayabilir; toplamdan hesaplanacakları için kalem olarak ekle
                    if (selectedPeriod === '4' && Array.isArray(sourceData)) {
                      const has690 = sourceData.some(g => (g.accountCode || '').startsWith('690') && g.accountCode.indexOf('.') === -1);
                      const has692 = sourceData.some(g => (g.accountCode || '').startsWith('692') && g.accountCode.indexOf('.') === -1);
                      sourceData = [...sourceData];
                      if (!has690) sourceData.push({ accountCode: '690', mainDescription: '', totalBorc: 0, totalAlacak: 0, totalBorcBakiyesi: 0, totalAlacakBakiyesi: 0 });
                      if (!has692) sourceData.push({ accountCode: '692', mainDescription: '', totalBorc: 0, totalAlacak: 0, totalBorcBakiyesi: 0, totalAlacakBakiyesi: 0 });
                      sourceData.sort((a, b) => (a.accountCode || '').localeCompare(b.accountCode || ''));
                    }
                    // Alt hesapları filtrele (sadece ana kodlar: nokta içermeyen)
                    const anaHesaplar = sourceData.filter(group => {
                      const accountCode = group.accountCode || '';
                      return accountCode.indexOf('.') === -1;
                    });
                    if (selectedPeriod === '4') {
                      const kalem590 = anaHesaplar.find(g => (g.accountCode || '').startsWith('590'));
                      const kalem591 = anaHesaplar.find(g => (g.accountCode || '').startsWith('591'));
                    }

                    if (anaHesaplar.length === 0) {
                      return (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Typography variant="body2" color="text.secondary">
                            Bilanço verisi bulunamadı
                          </Typography>
                        </Box>
                      );
                    }

                    // Toplamları hesapla
                    const totals = {
                      donenVarliklar: 0,
                      duranVarliklar: 0,
                      aktifToplam: 0,
                      kisaVadeliYukumlulukler: 0,
                      uzunVadeliYukumlulukler: 0,
                      sermaye: 0,
                      donemNetKari: 0,
                      pasifToplam: 0,
                      gelirler: 0,
                      giderler: 0,
                    };

                    // Kalemleri işle ve toplamları hesapla
                    const kalemler = [];
                    anaHesaplar.forEach((group, index) => {
                      const accountCode = group.accountCode || '';
                      const ilkHane = accountCode.charAt(0);
                      const debitBalance = parseFloat(group.totalBorcBakiyesi || 0);
                      const creditBalance = parseFloat(group.totalAlacakBakiyesi || 0);
                      
                      let bilancoDegeri = 0;
                      const shouldInvertAktifPasifForPeriod4 =
                        selectedPeriod === '4' &&
                        (ilkHane === '1' || ilkHane === '2' || ilkHane === '3' || ilkHane === '4' || ilkHane === '5');
                      
                      // Aktif hesaplar (1 ve 2 ile başlayan): debit - credit
                      if (ilkHane === '1' || ilkHane === '2') {
                        bilancoDegeri = debitBalance - creditBalance;
                        if (shouldInvertAktifPasifForPeriod4) bilancoDegeri = -bilancoDegeri;
                        
                        if (ilkHane === '1') {
                          totals.donenVarliklar += bilancoDegeri;
                        } else if (ilkHane === '2') {
                          totals.duranVarliklar += bilancoDegeri;
                        }
                      }
                      // Pasif hesaplar (3, 4, 5 ile başlayan): credit - debit
                      else if (ilkHane === '3' || ilkHane === '4' || ilkHane === '5') {
                        bilancoDegeri = creditBalance - debitBalance;
                        if (shouldInvertAktifPasifForPeriod4) bilancoDegeri = -bilancoDegeri;
                        
                        if (ilkHane === '3') {
                          totals.kisaVadeliYukumlulukler += bilancoDegeri;
                        } else if (ilkHane === '4') {
                          totals.uzunVadeliYukumlulukler += bilancoDegeri;
                        } else if (ilkHane === '5') {
                          // 590 veya 591 kontrolü (şimdilik sadece göster)
                          if (accountCode.startsWith('590') || accountCode.startsWith('591')) {
                            // 590/591 için şimdilik sadece göster, hesaplama yapma
                            totals.donemNetKari += bilancoDegeri;
                          } else {
                            totals.sermaye += bilancoDegeri;
                          }
                        }
                      }
                      // Gelir hesapları (6 ile başlayan)
                      else if (ilkHane === '6') {
                        if (selectedPeriod === '4') {
                          // 4. dönem: hareket tutarı; işaret sadece hesap koduna göre (60x +, 61x–63x -, 64x +, 65x–66x -, 67x +, 68x -, 690/692 toplamdan, 691 -, 697–698 +)
                          const totalBorc = parseFloat(group.totalBorc || 0);
                          const totalAlacak = parseFloat(group.totalAlacak || 0);
                          const magnitude = totalBorc !== 0 ? totalBorc : totalAlacak;
                          const codeNum = parseFloat(String(accountCode).trim().split('.')[0]) || 0;
                          const isNegatif = (codeNum >= 610 && codeNum <= 639) || (codeNum >= 650 && codeNum <= 669) || (codeNum >= 680 && codeNum <= 689) || codeNum === 691;
                          bilancoDegeri = isNegatif ? -magnitude : magnitude;
                        } else {
                          bilancoDegeri = creditBalance - debitBalance;
                        }
                        totals.gelirler += bilancoDegeri;
                      }
                      // Gider hesapları (7 ile başlayan): debit - credit
                      else if (ilkHane === '7') {
                        bilancoDegeri = debitBalance - creditBalance;
                        totals.giderler += bilancoDegeri;
                      }

                      kalemler.push({ 
                        type: 'kalem', 
                        accountCode,
                        accountName: group.mainDescription || '',
                        bilancoDegeri,
                        ilkHane,
                        index
                      });
                    });

                    // 4. dönem: 690 her türlü 600–689 toplamı; 692 = 691 varsa 690+691, yoksa 692=690
                    if (selectedPeriod === '4') {
                      const gelirKalemleri = kalemler.filter(k => k.ilkHane === '6');
                      const toplam600dan690a = gelirKalemleri
                        .filter(k => {
                          const code = parseFloat(String(k.accountCode).trim().split('.')[0]);
                          return !Number.isNaN(code) && code >= 600 && code < 690;
                        })
                        .reduce((sum, k) => sum + (k.bilancoDegeri || 0), 0);
                      const kalem690 = kalemler.find(k => k.ilkHane === '6' && (k.accountCode === '690' || (k.accountCode && k.accountCode.startsWith('690.'))));
                      if (kalem690) {
                        kalem690.bilancoDegeri = toplam600dan690a;
                      }
                      const kalem691 = kalemler.find(k => k.ilkHane === '6' && (k.accountCode === '691' || (k.accountCode && k.accountCode.startsWith('691.'))));
                      const kalem692 = kalemler.find(k => k.ilkHane === '6' && (k.accountCode === '692' || (k.accountCode && k.accountCode.startsWith('692.'))));
                      if (kalem692) {
                        const deger690 = kalem690 ? kalem690.bilancoDegeri : 0;
                        const deger691 = kalem691 ? (kalem691.bilancoDegeri || 0) : 0;
                        kalem692.bilancoDegeri = kalem691 ? deger690 + deger691 : deger690;
                      }
                    }

                    // Dönem Net Karı/Zararı hesapla (Gelirler - Giderler)
                    const hesaplananDonemNetKari = totals.gelirler - totals.giderler;
                    
                    // Dönem net karının %25'ini hesapla (sadece kar durumunda, 360 hesabına eklenecek) — 4. dönemde 360 sadece mizandan
                    const vergiMatrahi = hesaplananDonemNetKari > 0 ? hesaplananDonemNetKari * 0.25 : 0;
                    
                    if (selectedPeriod !== '4') {
                      const hesap360 = kalemler.find(k => k.accountCode === '360' || k.accountCode.startsWith('360.'));
                      if (hesap360) {
                        hesap360.bilancoDegeri += vergiMatrahi;
                        totals.kisaVadeliYukumlulukler += vergiMatrahi;
                      } else {
                        totals.kisaVadeliYukumlulukler += vergiMatrahi;
                      }
                    }
                    
                    // 590/591 değeri = Dönem net karı - Vergi matrahı
                    const donemNetKariDegeri = hesaplananDonemNetKari - vergiMatrahi;
                    const donemNetKariDegeri2 = - vergiMatrahi;
                    // Aktif toplamları THP hiyerarşisine göre güncelle (1 = 10+11+12+..., 2 = 22+23+...)
                    const thpAktif = getThpAktifBalanceMapAndVal(sourceData, selectedPeriod);
                    totals.donenVarliklar = thpAktif.getVal('1');
                    totals.duranVarliklar = thpAktif.getVal('2');
                    // Pasif toplamları THP hiyerarşisine göre güncelle (3, 4)
                    const thpPasif = getThpPasifBalanceMapAndVal(sourceData, selectedPeriod);
                    totals.kisaVadeliYukumlulukler = thpPasif.getVal('3');
                    totals.uzunVadeliYukumlulukler = thpPasif.getVal('4');
                    // Toplamları hesapla
                    totals.aktifToplam = totals.donenVarliklar + totals.duranVarliklar;
                    totals.pasifToplam = totals.kisaVadeliYukumlulukler + 
                                         totals.uzunVadeliYukumlulukler + 
                                         totals.sermaye + 
                                         totals.donemNetKari +
                                         donemNetKariDegeri;

                    // Toplam satırlarını oluştur (4. dönemde Özkaynaklar = sermaye + 590/591 mizan; Pasif toplam = kisa+uzun+özkaynaklar)
                    const ozkaynaklarDeger = selectedPeriod === '4' ? (totals.sermaye + totals.donemNetKari) : (totals.sermaye + donemNetKariDegeri);
                    const pasifToplamDeger = selectedPeriod === '4'
                      ? totals.kisaVadeliYukumlulukler + totals.uzunVadeliYukumlulukler + ozkaynaklarDeger
                      : totals.pasifToplam;
                    const toplamSatirlari = [
                      { type: 'toplam', kod: '', baslik: 'DÖNEN VARLIKLAR', deger: totals.donenVarliklar, isBold: false, grup: 'aktif' },
                      { type: 'toplam', kod: '', baslik: 'DURAN VARLIKLAR', deger: totals.duranVarliklar, isBold: false, grup: 'aktif' },
                      { type: 'toplam', kod: '', baslik: 'AKTİF TOPLAMI', deger: totals.aktifToplam, isBold: true, grup: 'aktif' },
                      { type: 'toplam', kod: '', baslik: 'KISA VADELİ YÜKÜMLÜLÜKLER', deger: totals.kisaVadeliYukumlulukler, isBold: false, grup: 'pasif' },
                      { type: 'toplam', kod: '', baslik: 'UZUN VADELİ YÜKÜMLÜLÜKLER', deger: totals.uzunVadeliYukumlulukler, isBold: false, grup: 'pasif' },
                      { type: 'toplam', kod: donemNetKariDegeri >= 0 ? '590' : '591', baslik: donemNetKariDegeri >= 0 ? 'DÖNEM NET KARI' : 'DÖNEM NET ZARARI', deger: donemNetKariDegeri, isBold: false, grup: 'pasif' },
                      { type: 'toplam', kod: '', baslik: 'ÖZKAYNAKLAR', deger: ozkaynaklarDeger, isBold: false, grup: 'pasif' },
                      { type: 'toplam', kod: '', baslik: 'PASİF TOPLAMI', deger: pasifToplamDeger, isBold: true, grup: 'pasif' },
                    ];

                    // Aktif ve pasif kalemlerini ayır
                    const aktifKalemler = kalemler.filter(k => k.ilkHane === '1' || k.ilkHane === '2');
                    const pasifKalemler = kalemler.filter(k => k.ilkHane === '3' || k.ilkHane === '4' || k.ilkHane === '5');

                    // Aktif ve pasif sonuçlarını hazırla (helper function)
                    const prepareSonuc = (kalemlerList, toplamSatirlariList, grup) => {
                      const sonuc = [];
                      const grupKalemler = grup === 'aktif' ? aktifKalemler : pasifKalemler;
                      
                      if (grup === 'aktif') {
                        let donenVarliklarToplamEklendi = false;
                        let duranVarliklarToplamEklendi = false;

                        grupKalemler.forEach((item, idx) => {
                          sonuc.push(item);
                          
                          if (!donenVarliklarToplamEklendi && item.ilkHane === '2' && idx > 0 && grupKalemler[idx - 1].ilkHane === '1') {
                            sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[0]);
                            donenVarliklarToplamEklendi = true;
                          }
                        });

                        if (!donenVarliklarToplamEklendi && grupKalemler.some(k => k.ilkHane === '1')) {
                          let sonDonenIndex = -1;
                          for (let i = grupKalemler.length - 1; i >= 0; i--) {
                            if (grupKalemler[i].ilkHane === '1') {
                              sonDonenIndex = i;
                              break;
                            }
                          }
                          if (sonDonenIndex >= 0) {
                            let sonucIndex = -1;
                            for (let i = 0; i < sonuc.length; i++) {
                              if (sonuc[i].type === 'kalem' && sonuc[i].index === grupKalemler[sonDonenIndex].index) {
                                sonucIndex = i;
                                break;
                              }
                            }
                            if (sonucIndex >= 0) {
                              sonuc.splice(sonucIndex + 1, 0, toplamSatirlariList[0]);
                            }
                          }
                        }

                        if (!duranVarliklarToplamEklendi && grupKalemler.some(k => k.ilkHane === '2')) {
                          let sonDuranIndex = -1;
                          for (let i = sonuc.length - 1; i >= 0; i--) {
                            if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '2') {
                              sonDuranIndex = i;
                              break;
                            }
                          }
                          if (sonDuranIndex >= 0) {
                            sonuc.splice(sonDuranIndex + 1, 0, toplamSatirlariList[1]);
                          }
                        }
                      } else {
                        // Pasif için
                        let kisaVadeliToplamEklendi = false;
                        let uzunVadeliToplamEklendi = false;
                        let sermayeToplamEklendi = false;
                        let donemNetKariEklendi = false;

                        grupKalemler.forEach((item, idx) => {
                          sonuc.push(item);
                          
                          if (!kisaVadeliToplamEklendi && item.ilkHane === '4' && idx > 0 && grupKalemler[idx - 1].ilkHane === '3') {
                            sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[0]);
                            kisaVadeliToplamEklendi = true;
                          }

                          if (!uzunVadeliToplamEklendi && item.ilkHane === '5' && !item.accountCode.startsWith('590') && !item.accountCode.startsWith('591') && idx > 0 && grupKalemler[idx - 1].ilkHane === '4') {
                            sonuc.splice(sonuc.length - 1, 0, toplamSatirlariList[1]);
                            uzunVadeliToplamEklendi = true;
                          }

                        });

                        if (!kisaVadeliToplamEklendi && grupKalemler.some(k => k.ilkHane === '3')) {
                          let sonKisaVadeliIndex = -1;
                          for (let i = sonuc.length - 1; i >= 0; i--) {
                            if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '3') {
                              sonKisaVadeliIndex = i;
                              break;
                            }
                          }
                          if (sonKisaVadeliIndex >= 0) {
                            sonuc.splice(sonKisaVadeliIndex + 1, 0, toplamSatirlariList[0]);
                          }
                        }

                        if (!uzunVadeliToplamEklendi && grupKalemler.some(k => k.ilkHane === '4')) {
                          let sonUzunVadeliIndex = -1;
                          for (let i = sonuc.length - 1; i >= 0; i--) {
                            if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '4') {
                              sonUzunVadeliIndex = i;
                              break;
                            }
                          }
                          if (sonUzunVadeliIndex >= 0) {
                            sonuc.splice(sonUzunVadeliIndex + 1, 0, toplamSatirlariList[1]);
                          }
                        }

                        // 590/591 toplam satırını ekle (500'lü hesaplardan sonra, ÖZKAYNAKLAR'dan önce) — 4. dönemde bu satır yok
                        if (!donemNetKariEklendi && donemNetKariDegeri !== 0 && selectedPeriod !== '4') {
                          // 500'lü hesaplardan sonra ekle
                          let sonSermayeHesabiIndex = -1;
                          for (let i = sonuc.length - 1; i >= 0; i--) {
                            if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '5' && !sonuc[i].accountCode.startsWith('590') && !sonuc[i].accountCode.startsWith('591')) {
                              sonSermayeHesabiIndex = i;
                              break;
                            }
                          }
                          if (sonSermayeHesabiIndex >= 0) {
                            sonuc.splice(sonSermayeHesabiIndex + 1, 0, toplamSatirlariList[2]);
                            donemNetKariEklendi = true;
                          } else {
                            // Eğer 500'lü hesap yoksa, uzun vadeli yükümlülüklerden sonra ekle
                            let uzunVadeliIndex = -1;
                            for (let i = sonuc.length - 1; i >= 0; i--) {
                              if (sonuc[i].type === 'toplam' && sonuc[i].baslik === 'UZUN VADELİ YÜKÜMLÜLÜKLER') {
                                uzunVadeliIndex = i;
                                break;
                              }
                            }
                            if (uzunVadeliIndex >= 0) {
                              sonuc.splice(uzunVadeliIndex + 1, 0, toplamSatirlariList[2]);
                              donemNetKariEklendi = true;
                            }
                          }
                        }

                        // ÖZKAYNAKLAR'ı ekle (590/591 toplam satırından sonra; 4. dönemde 590/591 toplam satırı yok, tüm 5'lilerden sonra)
                        if (!sermayeToplamEklendi && grupKalemler.some(k => k.ilkHane === '5')) {
                          let donemNetKariIndex = -1;
                          if (selectedPeriod !== '4') {
                            for (let i = sonuc.length - 1; i >= 0; i--) {
                              if (sonuc[i].type === 'toplam' && (sonuc[i].baslik === 'DÖNEM NET KARI' || sonuc[i].baslik === 'DÖNEM NET ZARARI')) {
                                donemNetKariIndex = i;
                                break;
                              }
                            }
                          }
                          if (donemNetKariIndex >= 0) {
                            sonuc.splice(donemNetKariIndex + 1, 0, toplamSatirlariList[3]);
                            sermayeToplamEklendi = true;
                          } else {
                            // 4. dönemde veya 590/591 toplam satırı yoksa: son 5'li hesaptan (590/591 dahil) sonra ÖZKAYNAKLAR ekle
                            let son5liIndex = -1;
                            for (let i = sonuc.length - 1; i >= 0; i--) {
                              if (sonuc[i].type === 'kalem' && sonuc[i].ilkHane === '5') {
                                son5liIndex = i;
                                break;
                              }
                            }
                            if (son5liIndex >= 0) {
                              sonuc.splice(son5liIndex + 1, 0, toplamSatirlariList[3]);
                              sermayeToplamEklendi = true;
                            }
                          }
                        }
                      }

                      return sonuc;
                    };

                    // Aktif tablosu Tekdüzen Hesap Planı (THP) hiyerarşisine göre: 1, 10, 100, 101, ...
                    const tdhOrder = (Array.isArray(tdhList) ? tdhList : [])
                      .map((x) => ({ code: String(x?.code || '').trim(), name: String(x?.name || '').trim() }))
                      .filter((x) => x.code && x.name && x.code.indexOf('.') === -1);
                    const aktifOrder = tdhOrder.filter((x) => (x.code.startsWith('1') || x.code.startsWith('2')) && x.code.length <= 3);
                    const pasifOrder = tdhOrder.filter((x) => (x.code.startsWith('3') || x.code.startsWith('4') || x.code.startsWith('5')) && x.code.length <= 3);
                    const gelirOrder = tdhOrder.filter(
                      (x) =>
                        x.code.startsWith('6') &&
                        x.code.length <= 3 &&
                        x.code !== '6' &&
                        x.code !== '69'
                    );

                    const aktifSonuc = buildAktifThpSonuc(sourceData, selectedPeriod, aktifOrder);
                    // Pasif tablosu hiyerarşisi: 3, 30, 300, ... 4, 40, ... 5, 50, ...
                    const pasifSonuc = buildPasifThpSonuc(sourceData, selectedPeriod, pasifOrder);
                    const gelirSonuc = buildGelirThpSonuc(sourceData, selectedPeriod, gelirOrder);
                    const maxRows = Math.max(aktifSonuc.length, pasifSonuc.length) + 1;

                    bilancoExportRef.current = {
                      aktifSonuc,
                      pasifSonuc,
                      gelirSonuc,
                      kalemler,
                      hesaplananDonemNetKari,
                      donemNetKariDegeri,
                      donemNetKariDegeri2,
                      selectedPeriod,
                      pasifToplamDeger,
                      aktifToplamDeger: totals.aktifToplam,
                    };

                    return (
                      <TrialBalanceSheetView
                        aktifSonuc={aktifSonuc}
                        pasifSonuc={pasifSonuc}
                        gelirSonuc={gelirSonuc}
                        totals={totals}
                        pasifToplamDeger={pasifToplamDeger}
                        selectedPeriod={selectedPeriod}
                        donemNetKariDegeri={donemNetKariDegeri}
                        hesaplananDonemNetKari={hesaplananDonemNetKari}
                        donemNetKariDegeri2={donemNetKariDegeri2}
                        formatNumber={formatNumber}
                      />
                    );
                  })()}

                  {viewMode === 'period' && activeTab === 1 && (!groupedMizanData || groupedMizanData.length === 0) && !(selectedPeriod === '4' && (period4BilancoFromYevmiye?.length > 0 || period4BilancoLoading || period4BilancoError)) && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <AccountBalance sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                        Bilanço Verisi Bulunamadı
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Trial balance verisi bulunamadı. Lütfen bir dönem seçin.
                      </Typography>
                    </Box>
                  )}

              </div>
            )}

            {/* Empty State */}
            {!mizanData && !loading && !selectedDocument && !selectedPeriod && (
              <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 dark:border-white/10 rounded-2xl">
                <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl mb-4">
                  <Scale size={40} className="text-gray-300 dark:text-gray-600" />
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {t('edefter.noTrialBalanceSelected')}
                </p>
                <p className="text-xs text-gray-400 text-center max-w-sm">
                  {t('edefter.noTrialBalanceSelectedHint')}
                </p>
              </div>
            )}

              </>
            ) : (
              <TrialBalanceFromFileTab
                uploadedMizansRefreshToken={uploadedMizansRefreshToken}
              />
            )}
        </div>
      </div>

      {pdfPreviewOpen && pdfFile && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1C1C1E] rounded-3xl shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/10 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center shrink-0">
                  <Eye size={18} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-bold truncate dark:text-white">{t('edefter.documentPreview')}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{pdfFileName || t('edefter.pdfDocumentLabel')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPdfPreviewOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors shrink-0 dark:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4">
              <iframe
                src={pdfFile}
                className="w-full h-full rounded-xl border border-gray-200 dark:border-white/10"
                title="PDF Preview"
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {uploadDialogOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={uploadLoading ? undefined : closeUploadModal}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-lg bg-white dark:bg-[#1C1C1E] rounded-[28px] border border-gray-100 dark:border-white/10 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#24299d]/10 dark:bg-[#24299d]/20 rounded-xl">
                  <Upload size={18} className="text-[#24299d] dark:text-[#818cf8]" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {t('edefter.uploadTrialBalance')}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeUploadModal}
                disabled={uploadLoading}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleUploadSubmit} className="p-6 space-y-4">
              {uploadError && (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl px-4 py-3">
                  <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600 dark:text-red-400 font-medium">{uploadError}</p>
                </div>
              )}
              {uploadSuccess && !uploadError && (
                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/40 rounded-xl px-4 py-3">
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                    Trial balance uploaded and parsed successfully.
                  </p>
                </div>
              )}

              <input
                ref={uploadFileInputRef}
                id="trial-balance-file-input"
                type="file"
                accept=".xlsx,.xls,.pdf"
                onChange={handleUploadFileChange}
                className="hidden"
              />

              {!uploadSelectedFile ? (
                <button
                  type="button"
                  onClick={() => uploadFileInputRef.current?.click()}
                  className="w-full p-8 border-2 border-dashed border-[#24299d]/40 dark:border-[#818cf8]/40 rounded-2xl text-center hover:bg-[#24299d]/5 dark:hover:bg-[#24299d]/10 transition-colors"
                >
                  <Upload size={40} className="mx-auto text-[#24299d] dark:text-[#818cf8] mb-3" />
                  <p className="text-sm font-semibold text-[#24299d] dark:text-[#818cf8] mb-1">
                    {t('edefter.uploadTrialBalance')}
                  </p>
                  <p className="text-xs text-gray-400">
                    Excel (.xlsx, .xls) or PDF (.pdf)
                  </p>
                </button>
              ) : (
                <div className="flex items-center justify-between gap-3 p-4 border-2 border-[#24299d]/30 dark:border-[#818cf8]/30 rounded-2xl bg-gray-50 dark:bg-white/5">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {uploadSelectedFile.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {(uploadSelectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadSelectedFile(null);
                      setUploadError(null);
                      setUploadSuccess(false);
                      setUploadProgress(0);
                    }}
                    disabled={uploadLoading}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    aria-label="Delete selected file"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {uploadLoading && (
                <div className="space-y-2">
                  <div className="h-2 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#24299d] rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center">
                    {uploadProgress < 70
                      ? `${t('edefter.uploadProgressUploading')} ${uploadProgress}%`
                      : uploadProgress < 100
                        ? `${t('edefter.uploadProgressProcessing')} ${uploadProgress}%`
                        : t('edefter.uploadProgressDone')}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={uploadLoading}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 rounded-xl transition-colors disabled:opacity-50"
                >
                  {t('company.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={uploadLoading || !uploadSelectedFile || !companyId || !taxId}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#24299d] hover:bg-[#1b2080] text-white rounded-xl text-sm font-semibold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t('edefter.uploading')}
                    </>
                  ) : (
                    t('edefter.uploadAndGenerate')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* PDF Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-pdf-dialog-title"
        aria-describedby="delete-pdf-dialog-description"
      >
        <DialogTitle id="delete-pdf-dialog-title">
          Delete PDF
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-pdf-dialog-description">
            Are you sure you want to delete this PDF file? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDeletePdf} 
            color="error" 
            variant="contained"
            disabled={deletingPdf}
            startIcon={deletingPdf ? <CircularProgress size={16} /> : <Delete />}
          >
            {deletingPdf ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TrialBalance;



